/**
 * InventoryPage.js - Gestão de Inventário
 * Fase D - UI/UX Overhaul
 * 
 * Features:
 * - Abas por categoria principal (LP, MB, CN, etc.)
 * - Sub-tabs para categorias com muitas subcategorias (Gaming Acessórios)
 * - Filtros globais reutilizáveis (Search, Location, State, Trackable)
 * - Dropdown de subcategoria dentro de cada tab
 * - Badge de localização visível em todos os produtos
 * - Gestão de Números de Série (CRUD completo)
 */

import { useEffect, useState, useMemo, useCallback, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { inventoryAPI, itemsAPI, procurementAPI, categoriesAPI, catalogsAPI, settingsAPI, getErrorMessage } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Checkbox } from '@/components/ui/checkbox';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ScrollArea, ScrollBar } from '@/components/ui/scroll-area';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { toast } from 'sonner';
import SerialNumbersPanel from '@/components/inventory/SerialNumbersPanel';
import { 
  Plus, Package, AlertCircle, Eye, Edit, PlusCircle, Search, 
  Smartphone, Monitor, Cpu, AlertTriangle, Check, Laptop, Settings, 
  Trash2, History, RotateCcw, Clock, User, MapPin, Warehouse, Store, 
  Truck, Gamepad2, Tv, TabletSmartphone, BatteryCharging, Zap, Hash, 
  FileText, Car, LayoutGrid, Filter, X, Layers, Printer, Barcode, Shield, MoreHorizontal, ArrowRightLeft, ChevronUp, ChevronDown, Boxes
} from 'lucide-react';
import { formatCurrency, formatDateTime, cn } from '@/lib/utils';
import { useAuth } from '@/hooks/useAuth';
import { hideResizeGuide, showResizeGuide } from '@/lib/resizeGuide';
import DynamicSpecsForm from '@/components/DynamicSpecsForm';
import ImportWizard from '@/components/ImportWizard';
import { LabelPrinter } from '@/components/LabelPrinter';
import { ProductPosDescriptionConfigurator } from '@/components/inventory/ProductPosDescriptionConfigurator';
import { UniversalCrudActions } from '@/components/UniversalCrudActions';
import { DEFAULT_TABLE_PAGE_SIZE, paginateItems, TablePaginationBar } from '@/components/common/TablePaginationBar';
import { buildPosDescriptionPreview, resolveCatalogPosDescriptionRule, resolveProductSchemaKey } from '@/lib/productPosDescription';
import { resolveCatalogCategoryPrefix } from '@/lib/productCatalog';
import { buildProductHighlightItems, buildProductHighlightPayload, buildProductHighlightSummary } from '@/lib/productHighlightAttributes';
import { ModuleStateBoundary } from '@/components/common/ModuleStateBoundary';
import { EmptyStatePanel } from '@/components/common/EmptyStatePanel';
import { safeRequest } from '@/lib/safeRequest';
import { useFetchTelemetry } from '@/hooks/useFetchTelemetry';
import { TABLE_DENSITY } from '@/lib/tableDensity';
import ExportButton from '@/components/ExportButton';

const PRIMARY_ATTRIBUTE_RULE_DEFAULTS = [
  { key: 'color', label: 'Cor', active: true, required: false, highlight: true, variant_policy: 'attribute_only' },
  { key: 'power', label: 'Potência', active: true, required: false, highlight: true, variant_policy: 'attribute_only' },
  { key: 'warranty', label: 'Garantia', active: true, required: false, highlight: true, variant_policy: 'attribute_only' },
  { key: 'connector', label: 'Conector', active: true, required: false, highlight: true, variant_policy: 'attribute_only' },
];

const VARIANT_POLICY_LABELS = {
  attribute_only: 'Só atributo',
  variant_optional: 'Variante opcional',
  variant_required: 'Variante obrigatória',
};

const PRIMARY_ATTRIBUTE_PLACEHOLDERS = {
  color: 'Ex: Preto mate',
  power: 'Ex: 65 W',
  warranty: 'Ex: 12 meses',
  connector: 'Ex: USB-C',
};

const CONDITION_OPTIONS = [
  { value: 'new', label: 'Novo' },
  { value: 'sealed', label: 'Selado' },
  { value: 'excellent', label: 'Excelente' },
  { value: 'good', label: 'Bom' },
  { value: 'fair', label: 'Razoável' },
];

const INVENTORY_STATE_OPTIONS = [
  { value: 'all', label: 'Todos Estados', shortLabel: 'Todos' },
  { value: 'in_stock', label: 'Em Stock', shortLabel: 'Stock' },
  { value: 'reserved', label: 'Reservado', shortLabel: 'Reserv.' },
  { value: 'sold', label: 'Vendido', shortLabel: 'Vend.' },
  { value: 'repair', label: 'Em Reparação', shortLabel: 'Repar.' },
  { value: 'defective', label: 'Com Defeito', shortLabel: 'Defeito' },
  { value: 'in_transit', label: 'Em Trânsito', shortLabel: 'Trânsito' },
  { value: 'returned', label: 'Devolvido', shortLabel: 'Dev.' },
  { value: 'offered', label: 'Oferta', shortLabel: 'Oferta' },
  { value: 'internal_use', label: 'Uso Interno', shortLabel: 'Interno' },
  { value: 'lost', label: 'Perdido', shortLabel: 'Perdido' },
  { value: 'scrapped', label: 'Abatido', shortLabel: 'Abatido' },
];

const INVENTORY_SORT_OPTIONS = [
  { value: 'stock', label: 'Stock' },
  { value: 'product', label: 'Produto' },
  { value: 'code', label: 'Código' },
  { value: 'classification', label: 'Classificação' },
  { value: 'summary', label: 'Resumo técnico' },
  { value: 'price', label: 'Preço' },
];

const DEFAULT_INVENTORY_COLUMN_WIDTHS = [4, 19, 10, 11, 18, 8, 8, 6, 5];
const MIN_INVENTORY_COLUMN_WIDTHS = [4, 12, 8, 9, 12, 6, 6, 5, 5];
const INVENTORY_HEAD_CLASS = TABLE_DENSITY.head.replace('tracking-[0.16em]', 'tracking-[0.14em]');

const PRODUCT_CREATE_ERROR_FIELD_LABELS = {
  name: 'Nome do produto',
  sku: 'SKU / Código',
  barcode: 'Código de barras',
  category: 'Categoria',
  subcategory_id: 'Subcategoria',
  unit_price: 'Preço de venda',
  cost_price: 'Preço de custo',
  reorder_level: 'Nível de reposição',
  initial_stock: 'Stock inicial',
  is_trackable: 'Tipo de artigo',
  year: 'Ano',
};

// Icon mapping for location types
const LOCATION_TYPE_ICONS = {
  warehouse: Warehouse,
  store: Store,
  transit: Truck,
  repair: Settings,
  showcase: Eye,
};

// Icon mapping for product categories (base_prefix)
const CATEGORY_ICONS = {
  'LP': Laptop,
  'DS': Monitor,
  'SW': Cpu,
  'AI': Monitor,
  'AIO': Monitor,
  'SC': Tv,
  'MN': Tv,
  'CN': Gamepad2,
  'GA': Gamepad2,
  'MB': Smartphone,
  'SM': Smartphone,
  'TB': TabletSmartphone,
  'AM': Smartphone,
  'CHG': BatteryCharging,
  'TRA': Zap,
  'PAP': FileText,
  'PC': Settings,
  'CAR': Car,
  'DV': Package,
};

// Category labels: full name (≥1024px) and short name (<1024px)
const CATEGORY_LABELS = {
  'LP': { full: 'Laptop', short: 'Laptop' },
  'DS': { full: 'Desktop', short: 'Desktop' },
  'SW': { full: 'Servidores', short: 'Server' },
  'AI': { full: 'All-in-One', short: 'AiO' },
  'AIO': { full: 'All-in-One', short: 'AiO' },
  'SC': { full: 'Monitores/TVs', short: 'Monitor' },
  'MN': { full: 'Monitores/TVs', short: 'Monitor' },
  'CN': { full: 'Consolas', short: 'Consola' },
  'GA': { full: 'Gaming Acessórios', short: 'Gaming' },
  'MB': { full: 'Smartphones', short: 'Phone' },
  'SM': { full: 'Smartphones', short: 'Phone' },
  'TB': { full: 'Tablet', short: 'Tablet' },
  'AM': { full: 'Acessórios Mobile', short: 'Acess.' },
  'CHG': { full: 'Carregadores', short: 'Carreg.' },
  'TRA': { full: 'Transformadores', short: 'Transf.' },
  'PAP': { full: 'Papelaria', short: 'Papel.' },
  'PC': { full: 'Peças e Componentes', short: 'Peças' },
  'CAR': { full: 'Carro', short: 'Carro' },
  'DV': { full: 'Diversos', short: 'Outros' },
};

const CATEGORY_TAB_CANONICAL_MAP = {
  AIO: 'AI',
  MN: 'SC',
  SM: 'MB',
};

// Tab display order and configuration
const TAB_ORDER = [
  'LP', 'DS', 'SW', 'AI', 'AIO', 'SC', 'MN', 'CN', 'GA', 'MB', 'SM', 'TB', 'AM', 'CHG', 'TRA', 'PAP', 'PC', 'CAR', 'DV'
];

const getCanonicalCategoryTab = (value) => {
  const normalized = String(value || '').trim().toUpperCase();
  return CATEGORY_TAB_CANONICAL_MAP[normalized] || normalized;
};

const getTabCategoryPrefixes = (value) => {
  const canonical = getCanonicalCategoryTab(value);
  return Array.from(new Set([
    canonical,
    ...Object.entries(CATEGORY_TAB_CANONICAL_MAP)
      .filter(([, target]) => target === canonical)
      .map(([alias]) => alias),
  ]));
};

const normalizeCategoryTab = (value) => {
  if (!value) return 'all';
  if (String(value).toLowerCase() === 'all') return 'all';
  return getCanonicalCategoryTab(value);
};

// Categories that have many subcategories and should show sub-tabs
const CATEGORIES_WITH_SUBTABS = ['GA', 'AM', 'DV'];
const sanitizeCatalogList = (entries = []) => entries.filter((entry) => entry && typeof entry === 'object');
const INVENTORY_COLUMNS_STORAGE_KEY = 'inventory-operational-columns-v1';
const readInventoryColumnWidths = () => {
  if (typeof window === 'undefined') return DEFAULT_INVENTORY_COLUMN_WIDTHS;
  try {
    const saved = window.sessionStorage.getItem(INVENTORY_COLUMNS_STORAGE_KEY);
    const parsed = saved ? JSON.parse(saved) : DEFAULT_INVENTORY_COLUMN_WIDTHS;
    return Array.isArray(parsed) && parsed.length === DEFAULT_INVENTORY_COLUMN_WIDTHS.length ? parsed : DEFAULT_INVENTORY_COLUMN_WIDTHS;
  } catch {
    return DEFAULT_INVENTORY_COLUMN_WIDTHS;
  }
};
const writeInventoryColumnWidths = (value) => {
  if (typeof window === 'undefined') return;
  window.sessionStorage.setItem(INVENTORY_COLUMNS_STORAGE_KEY, JSON.stringify(value));
};

export default function InventoryPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  
  // Data state
  const [products, setProducts] = useState([]);
  const [selectedProductIds, setSelectedProductIds] = useState([]);
  const [inventoryColumnWidths, setInventoryColumnWidths] = useState(readInventoryColumnWidths);
  const inventoryTableWrapperRef = useRef(null);
  const inventoryColumnResizeRef = useRef(null);
  const [deletedProducts, setDeletedProducts] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [categories, setCategories] = useState([]); // Legacy categories from /categories
  const [productCategories, setProductCategories] = useState([]); // New catalog categories
  const [productSubcategories, setProductSubcategories] = useState([]);
  const [inventorySummary, setInventorySummary] = useState({ total_products: 0, total_quantity: 0, low_stock_count: 0, zero_stock_count: 0, stock_cost_value: 0, stock_sale_value: 0, outbound_units_90d: 0, rotation_ratio_90d: 0, integrity: {}, top_categories: [] });
  const [schemas, setSchemas] = useState({});
  const [locations, setLocations] = useState([]);
  const [warrantyOptions, setWarrantyOptions] = useState([]); // P0.2: Warranty options
  const [loading, setLoading] = useState(true);
  const { partialIssues, captureFetchTelemetry, resetFetchTelemetry } = useFetchTelemetry();
  const [auxiliaryCatalogsLoaded, setAuxiliaryCatalogsLoaded] = useState(false);
  const [auxiliaryCatalogsLoading, setAuxiliaryCatalogsLoading] = useState(false);
  
  // Tab state
  const [activeTab, setActiveTab] = useState(normalizeCategoryTab(searchParams.get('tab')));
  const [activeSubTab, setActiveSubTab] = useState('all');
  const [subcategoryFilter, setSubcategoryFilter] = useState('all');
  
  // Filter state (Global Filters)
  const [searchQuery, setSearchQuery] = useState('');
  const [locationFilter, setLocationFilter] = useState('all');
  const [stateFilter, setStateFilter] = useState('all');
  const [trackableFilter, setTrackableFilter] = useState('all');
  const [showDeleted, setShowDeleted] = useState(false);
  const [inventorySortField, setInventorySortField] = useState('stock');
  const [inventorySortDirection, setInventorySortDirection] = useState('desc');
  const [inventoryPage, setInventoryPage] = useState(1);
  
  // Dialog state
  const [dialogOpen, setDialogOpen] = useState(false);
  const [itemDialogOpen, setItemDialogOpen] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [productItems, setProductItems] = useState([]);
  const [stockByLocation, setStockByLocation] = useState({});
  const [locationAvailabilityMap, setLocationAvailabilityMap] = useState({});
  const [stateFilteredProductIds, setStateFilteredProductIds] = useState(null);
  const [specsErrors, setSpecsErrors] = useState([]);
  const [createProductError, setCreateProductError] = useState('');
  const [stockAdjustmentDialogOpen, setStockAdjustmentDialogOpen] = useState(false);
  const [stockAdjustmentProduct, setStockAdjustmentProduct] = useState(null);
  const [stockAdjustmentForm, setStockAdjustmentForm] = useState({ quantity: '1', reason: '', location_id: '' });
  
  // Edit state
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editFormData, setEditFormData] = useState(null);
  const [editJustification, setEditJustification] = useState('');
  const [confirmEditDialogOpen, setConfirmEditDialogOpen] = useState(false);
  
  // Import state
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [bulkUpdateDialogOpen, setBulkUpdateDialogOpen] = useState(false);
  const [bulkUpdateFile, setBulkUpdateFile] = useState(null);
  const [bulkUpdateUseSelection, setBulkUpdateUseSelection] = useState(true);
  const [bulkUpdateDownloading, setBulkUpdateDownloading] = useState(false);
  const [bulkUpdateUploading, setBulkUpdateUploading] = useState(false);
  const [bulkUpdatePreviewing, setBulkUpdatePreviewing] = useState(false);
  const [bulkUpdateSummary, setBulkUpdateSummary] = useState(null);
  
  // Delete state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteReason, setDeleteReason] = useState('');
  const [bulkDeleteDialogOpen, setBulkDeleteDialogOpen] = useState(false);
  const [bulkDeleteReason, setBulkDeleteReason] = useState('');
  const [bulkDeleteHardDelete, setBulkDeleteHardDelete] = useState(false);
  const [bulkDeleteLoading, setBulkDeleteLoading] = useState(false);
  
  // Audit state
  const [auditDialogOpen, setAuditDialogOpen] = useState(false);
  const [auditLogs, setAuditLogs] = useState([]);
  const [serialNumbersDialogOpen, setSerialNumbersDialogOpen] = useState(false);
  
  const { hasRole, user } = useAuth();
  const isAdmin = hasRole(['admin']);
  const isManager = hasRole(['store_manager']);
  const canEdit = hasRole(['admin', 'store_manager']);
  const safeProductCategories = useMemo(() => sanitizeCatalogList(productCategories), [productCategories]);
  const safeProductSubcategories = useMemo(() => sanitizeCatalogList(productSubcategories), [productSubcategories]);
  
  const initialFormData = {
    name: '',
    sku: '',
    barcode: '',
    description: '',
    brand: '',
    model: '',
    part_number: '',
    color: '',
    power: '',
    warranty: '',
    connector: '',
    category: '',
    subcategory: '',
    specs: {},
    pos_description_use_override: false,
    pos_description_fields: [],
    unit_price: '',
    cost_price: '',
    is_trackable: true,
    reorder_level: '10',
    initial_stock: '0',
    location_id: '',
    invalidSubcategory: false,
    warranty_option_id: '',  // P0.2: Warranty option
    warranty_days: '',  // P0.2: Cached warranty days
    variant_parent_product_id: '',
    variant_mode: 'standalone',
  };
  
  const [formData, setFormData] = useState(initialFormData);
  
  const initialItemData = {
    product_id: '',
    imei1: '',
    imei2: '',
    imei3: '',
    serial_number: '',
    condition: 'new',
    location_id: '',
    purchase_price: '',
    supplier: '',
    notes: ''
  };
  
  const [itemFormData, setItemFormData] = useState(initialItemData);
  
  // Autocomplete state
  const [nameSearchOpen, setNameSearchOpen] = useState(false);
  const [showDuplicateWarning, setShowDuplicateWarning] = useState(false);
  const [similarProducts, setSimilarProducts] = useState([]);
  const [skuPreview, setSkuPreview] = useState(null);
  const [skuPreviewLoading, setSkuPreviewLoading] = useState(false);
  const [skuManuallyEdited, setSkuManuallyEdited] = useState(false);
  
  // Mapping from base_prefix to schema category_key
  const PREFIX_TO_SCHEMA_KEY = {
    'LP': 'laptop',
    'DS': 'desktop',
    'MB': 'smartphone',
    'SC': 'monitor_tv',
    'CHG': 'carregador',
    'CN': 'consola',
    // Future mappings
    'SW': 'servidor',
    'AI': 'all_in_one',
    'AIO': 'all_in_one',
    'TB': 'tablet',
  };
  
  // Get current category schema using the mapping
  const getSchemaForCategory = (categoryPrefix) => {
    if (!categoryPrefix) return null;
    const category = safeProductCategories.find((item) => item.base_prefix === String(categoryPrefix).toUpperCase());
    if (category?.effective_schema) return category.effective_schema;
    const normalizedPrefix = String(categoryPrefix).toUpperCase();
    const schemaKey = PREFIX_TO_SCHEMA_KEY[normalizedPrefix] || resolveProductSchemaKey(categoryPrefix);
    return schemaKey ? schemas[schemaKey] : null;
  };

  const getCategoryGovernance = useCallback((categoryPrefix) => {
    if (!categoryPrefix) return null;
    return safeProductCategories.find((item) => item.base_prefix === String(categoryPrefix).toUpperCase()) || null;
  }, [safeProductCategories]);

  const getPrimaryAttributeRules = useCallback((schemaPayload) => {
    const rawRules = Array.isArray(schemaPayload?.primary_attribute_fields) && schemaPayload.primary_attribute_fields.length
      ? schemaPayload.primary_attribute_fields
      : PRIMARY_ATTRIBUTE_RULE_DEFAULTS;
    return PRIMARY_ATTRIBUTE_RULE_DEFAULTS.map((fallbackRule) => {
      const matched = rawRules.find((rule) => rule.key === fallbackRule.key) || {};
      return {
        ...fallbackRule,
        ...matched,
      };
    }).filter((rule) => rule.active !== false);
  }, []);

  const getVariantPolicySummary = useCallback((schemaPayload) => {
    const rules = getPrimaryAttributeRules(schemaPayload).filter((rule) => rule.variant_policy && rule.variant_policy !== 'attribute_only');
    if (!rules.length) return 'Nenhum atributo desta categoria obriga a nova variante.';
    return rules.map((rule) => `${rule.label}: ${VARIANT_POLICY_LABELS[rule.variant_policy] || rule.variant_policy}`).join(' • ');
  }, [getPrimaryAttributeRules]);

  const buildVariantParentProductId = useCallback((state, baseProduct = null) => {
    if (baseProduct?.id) return baseProduct.id;
    if (state?.variant_parent_product_id) return state.variant_parent_product_id;
    return null;
  }, []);
  
  const currentSchema = getSchemaForCategory(formData.category);
  const editCurrentSchema = getSchemaForCategory(editFormData?.category);
  const currentCategoryGovernance = getCategoryGovernance(formData.category);
  const editCategoryGovernance = getCategoryGovernance(editFormData?.category);
  const currentCatalogPosRule = useMemo(() => resolveCatalogPosDescriptionRule({
    categoryPrefix: formData.category,
    categoryId: productCategories.find((item) => item.base_prefix === formData.category)?.id,
    subcategoryId: formData.subcategory,
    categories: productCategories,
    subcategories: productSubcategories,
  }), [formData.category, formData.subcategory, productCategories, productSubcategories]);
  const editCatalogPosRule = useMemo(() => resolveCatalogPosDescriptionRule({
    categoryPrefix: editFormData?.category,
    categoryId: productCategories.find((item) => item.base_prefix === editFormData?.category)?.id,
    subcategoryId: editFormData?.subcategory,
    categories: productCategories,
    subcategories: productSubcategories,
  }), [editFormData?.category, editFormData?.subcategory, productCategories, productSubcategories]);

  // ==========================================================================
  // DATA LOADING
  // ==========================================================================

  const loadDeferredInventoryLookups = useCallback(async () => {
    try {
      const [categoriesRes, inventorySummaryRes] = await Promise.all([
        safeRequest(() => categoriesAPI.list(), { data: [] }, { telemetry: { module: 'inventory', endpoint: 'categories.list.deferred', onEvent: captureFetchTelemetry } }),
        safeRequest(() => inventoryAPI.getSummary(), { data: {} }, { telemetry: { module: 'inventory', endpoint: 'inventory.summary.deferred', onEvent: captureFetchTelemetry } }),
      ]);
      setCategories(categoriesRes.data || []);
      setInventorySummary(inventorySummaryRes.data || {});
    } catch (error) {
      console.error('Error loading deferred inventory lookups:', error);
    }
  }, [captureFetchTelemetry]);
  
  const loadInitialData = useCallback(async () => {
    try {
      setLoading(true);
      resetFetchTelemetry();
      const [
        productsRes, 
        locationsRes,
        productCategoriesRes,
        productSubcategoriesRes
      ] = await Promise.all([
        safeRequest(() => inventoryAPI.listProducts(), { data: [] }, { telemetry: { module: 'inventory', endpoint: 'inventory.products', onEvent: captureFetchTelemetry } }),
        safeRequest(() => settingsAPI.listActiveLocations(), { data: [] }, { telemetry: { module: 'inventory', endpoint: 'settings.locations', onEvent: captureFetchTelemetry } }),
        safeRequest(() => catalogsAPI.listProductCategories(), { data: [] }, { telemetry: { module: 'inventory', endpoint: 'catalogs.product_categories', onEvent: captureFetchTelemetry } }),
        safeRequest(() => catalogsAPI.listProductSubcategories(), { data: [] }, { telemetry: { module: 'inventory', endpoint: 'catalogs.product_subcategories', onEvent: captureFetchTelemetry } })
      ]);
      
      setProducts(productsRes.data || []);
      setLocations(locationsRes.data || []);
      setProductCategories(sanitizeCatalogList(productCategoriesRes.data || []));
      setProductSubcategories(sanitizeCatalogList(productSubcategoriesRes.data || []));
      void loadDeferredInventoryLookups();
    } catch (error) {
      toast.error('Erro ao carregar dados');
      console.error(error);
    } finally {
      setLoading(false);
    }
  }, [captureFetchTelemetry, loadDeferredInventoryLookups, resetFetchTelemetry]);

  useEffect(() => {
    loadInitialData();
  }, [loadInitialData]);
  
  // Update URL when tab changes
  useEffect(() => {
    if (activeTab && activeTab !== 'all') {
      setSearchParams({ tab: activeTab });
    } else {
      setSearchParams({});
    }
  }, [activeTab, setSearchParams]);

  const loadAuxiliaryCatalogs = useCallback(async () => {
    if (auxiliaryCatalogsLoaded || auxiliaryCatalogsLoading) return;

    setAuxiliaryCatalogsLoading(true);
    try {
      const [suppliersRes, schemasRes, warrantyOptionsRes] = await Promise.all([
        safeRequest(() => procurementAPI.listSuppliers(), { data: [] }, { telemetry: { module: 'inventory', endpoint: 'procurement.suppliers', onEvent: captureFetchTelemetry } }),
        safeRequest(() => categoriesAPI.getAllSchemas(), { data: {} }, { telemetry: { module: 'inventory', endpoint: 'categories.schemas', onEvent: captureFetchTelemetry } }),
        safeRequest(() => settingsAPI.listActiveWarrantyOptions(), { data: [] }, { telemetry: { module: 'inventory', endpoint: 'settings.warranty_options', onEvent: captureFetchTelemetry } }),
      ]);

      setSuppliers(suppliersRes.data || []);
      setSchemas(schemasRes.data || {});
      setWarrantyOptions(warrantyOptionsRes.data || []);
      setAuxiliaryCatalogsLoaded(true);
    } catch (error) {
      console.error('Error loading inventory auxiliary catalogs:', error);
    } finally {
      setAuxiliaryCatalogsLoading(false);
    }
  }, [auxiliaryCatalogsLoaded, auxiliaryCatalogsLoading, captureFetchTelemetry]);

  useEffect(() => {
    if (dialogOpen || editDialogOpen) {
      loadAuxiliaryCatalogs();
    }
  }, [dialogOpen, editDialogOpen, loadAuxiliaryCatalogs]);

  const loadProducts = useCallback(async () => {
    try {
      const [response, summaryResponse] = await Promise.all([
        safeRequest(() => inventoryAPI.listProducts(), { data: [] }, { telemetry: { module: 'inventory', endpoint: 'inventory.products.refresh', onEvent: captureFetchTelemetry } }),
        safeRequest(() => inventoryAPI.getSummary(), { data: {} }, { telemetry: { module: 'inventory', endpoint: 'inventory.summary.refresh', onEvent: captureFetchTelemetry } }),
      ]);
      setProducts(response.data || []);
      setInventorySummary(summaryResponse.data || {});
    } catch (error) {
      toast.error('Erro ao carregar produtos');
    }
  }, [captureFetchTelemetry]);

  const loadDeletedProducts = useCallback(async () => {
    try {
      const response = await safeRequest(() => inventoryAPI.listDeletedProducts(), { data: [] }, { telemetry: { module: 'inventory', endpoint: 'inventory.deleted_products', onEvent: captureFetchTelemetry } });
      setDeletedProducts(response.data || []);
    } catch (error) {
      toast.error('Erro ao carregar produtos eliminados');
    }
  }, [captureFetchTelemetry]);

  const refreshInventoryCollections = useCallback(async () => {
    await Promise.all([loadProducts(), loadDeletedProducts()]);
  }, [loadDeletedProducts, loadProducts]);

  useEffect(() => {
    let active = true;

    const loadLocationAvailability = async () => {
      if (!locationFilter || locationFilter === 'all') {
        setLocationAvailabilityMap({});
        return;
      }

      try {
        const response = await inventoryAPI.listPosAvailability({ location_id: locationFilter, limit: 500 });
        if (!active) return;

        const nextMap = {};
        (response.data?.products || []).forEach((product) => {
          nextMap[product.id] = {
            selected_location_available: Number(product.selected_location_available || 0),
            availability_by_location: product.availability_by_location || [],
          };
        });
        setLocationAvailabilityMap(nextMap);
      } catch (error) {
        if (!active) return;
        toast.error('Erro ao carregar stock por localização');
        setLocationAvailabilityMap({});
      }
    };

    loadLocationAvailability();

    return () => {
      active = false;
    };
  }, [locationFilter]);

  useEffect(() => {
    let active = true;

    const loadStateFilteredProducts = async () => {
      const normalizedState = stateFilter === 'in_repair' ? 'repair' : stateFilter;
      if (!normalizedState || normalizedState === 'all' || normalizedState === 'in_stock') {
        setStateFilteredProductIds(null);
        return;
      }

      try {
        const response = await itemsAPI.list({
          state: normalizedState,
          ...(locationFilter && locationFilter !== 'all' ? { location_id: locationFilter } : {}),
        });

        if (!active) return;
        setStateFilteredProductIds(new Set((response.data || []).map((item) => item.product_id).filter(Boolean)));
      } catch (error) {
        if (!active) return;
        toast.error('Erro ao validar estados do inventário');
        setStateFilteredProductIds(null);
      }
    };

    loadStateFilteredProducts();

    return () => {
      active = false;
    };
  }, [stateFilter, locationFilter]);

  // ==========================================================================
  // FILTERING LOGIC
  // ==========================================================================
  
  // Map product category to base_prefix
  const getCategoryPrefix = useCallback((product) => {
    const resolvedPrefix = resolveCatalogCategoryPrefix(
      product?.category || product?.category_name || product?.category_id,
      product?.sku,
    );
    if (resolvedPrefix) return resolvedPrefix;
    
    // Second: Check SKU prefix (for products created with new system)
    const sku = product.sku?.toUpperCase() || '';
    
    // Sort prefixes by length (longest first) to avoid false positives
    // e.g., "MBP" should not match "MB", "LPG" should not match "LP"
    const sortedPrefixes = [...TAB_ORDER].sort((a, b) => b.length - a.length);
    
    // Also check subcategory prefixes which are more specific
    for (const prefix of sortedPrefixes) {
      // Only match if it's the exact start and followed by a digit or specific patterns
      const prefixPattern = new RegExp(`^${prefix}[0-9A-Z]`);
      if (prefixPattern.test(sku) || sku === prefix) {
        return prefix;
      }
    }
    
    // Default to "Diversos" (DV) if no match
    return 'DV';
  }, []);

  const findProductSubcategory = useCallback((productOrValue) => {
    if (!productOrValue) return null;
    const safeSubcategoryList = sanitizeCatalogList(safeProductSubcategories);

    const candidateValues = typeof productOrValue === 'object'
      ? [productOrValue.subcategory_id, productOrValue.subcategory, productOrValue.category]
      : [productOrValue];

    for (const candidate of candidateValues) {
      if (!candidate) continue;
      const normalizedCandidate = String(candidate).trim().toLowerCase();
      const match = safeSubcategoryList.find((subcategory) => (
        subcategory.id === candidate
        || String(subcategory.prefix || '').trim().toLowerCase() === normalizedCandidate
        || String(subcategory.name || '').trim().toLowerCase() === normalizedCandidate
      ));
      if (match) return match;
    }

    return null;
  }, [safeProductSubcategories]);

  const findProductCategory = useCallback((productOrValue, fallbackProduct = null) => {
    const safeCategoryList = sanitizeCatalogList(safeProductCategories);
    const product = typeof productOrValue === 'object' ? productOrValue : fallbackProduct;
    const candidateValues = typeof productOrValue === 'object'
      ? [productOrValue.category_id, productOrValue.category]
      : [productOrValue];

    for (const candidate of candidateValues) {
      if (!candidate) continue;
      const normalizedCandidate = String(candidate).trim().toLowerCase();
      const match = safeCategoryList.find((category) => (
        category.id === candidate
        || String(category.base_prefix || '').trim().toLowerCase() === normalizedCandidate
        || String(category.name || '').trim().toLowerCase() === normalizedCandidate
      ));
      if (match) return match;
    }

    const matchedSubcategory = product ? findProductSubcategory(product) : null;
    if (matchedSubcategory) {
      const categoryFromSubcategory = safeCategoryList.find((category) => category.id === matchedSubcategory.category_id);
      if (categoryFromSubcategory) return categoryFromSubcategory;
    }

    if (product) {
      const fallbackPrefix = getCategoryPrefix(product);
      return safeCategoryList.find((category) => category.base_prefix === fallbackPrefix) || null;
    }

    return null;
  }, [findProductSubcategory, getCategoryPrefix, safeProductCategories]);
  
  // Get subcategories for a specific category
  const getSubcategoriesForCategory = useCallback((categoryPrefix) => {
    const category = findProductCategory(categoryPrefix);
    if (!category) return [];
    return sanitizeCatalogList(safeProductSubcategories).filter((subcategory) => subcategory.category_id === category.id);
  }, [findProductCategory, safeProductSubcategories]);

  const getProductClassificationState = useCallback((product) => {
    const category = findProductCategory(product);
    const availableSubcategories = category
      ? productSubcategories.filter((subcategory) => subcategory.category_id === category.id)
      : [];
    const matchedSubcategory = findProductSubcategory(product);
    const validSubcategory = matchedSubcategory && availableSubcategories.some((subcategory) => subcategory.id === matchedSubcategory.id)
      ? matchedSubcategory
      : null;

    return {
      category,
      categoryPrefix: category?.base_prefix || '',
      categoryId: category?.id || '',
      subcategory: validSubcategory,
      subcategoryId: validSubcategory?.id || '',
      invalidSubcategory: Boolean((product?.subcategory_id || product?.subcategory || matchedSubcategory) && !validSubcategory),
    };
  }, [findProductCategory, findProductSubcategory, productSubcategories]);

  const buildClassificationPayload = useCallback((state) => {
    const category = findProductCategory(state?.category);
    const availableSubcategories = category
      ? productSubcategories.filter((subcategory) => subcategory.category_id === category.id)
      : [];
    const subcategory = availableSubcategories.find((item) => item.id === state?.subcategory) || null;

    return {
      category: category?.base_prefix || state?.category || '',
      category_id: category?.id || null,
      subcategory_id: subcategory?.id || null,
    };
  }, [findProductCategory, productSubcategories]);

  const fetchSkuPreview = useCallback(async (state) => {
    const payload = buildClassificationPayload(state);
    if (!payload.category && !payload.subcategory_id) {
      setSkuPreview(null);
      return;
    }

    try {
      setSkuPreviewLoading(true);
      const response = await inventoryAPI.previewSku(payload);
      setSkuPreview(response.data);
      return response.data;
    } catch (error) {
      setSkuPreview(null);
      return null;
    } finally {
      setSkuPreviewLoading(false);
    }
  }, [buildClassificationPayload]);

  useEffect(() => {
    if (skuManuallyEdited && formData.sku) return;
    if (!formData.category) {
      setSkuPreview(null);
      return;
    }
    fetchSkuPreview({
      category: formData.category,
      subcategory: formData.subcategory,
      sku: formData.sku,
    });
  }, [formData.category, formData.subcategory, skuManuallyEdited, formData.sku, fetchSkuPreview]);

  const getProductSubcategoryLabel = useCallback((product) => {
    return getProductClassificationState(product).subcategory?.name || null;
  }, [getProductClassificationState]);

  const getProductCategoryLabel = useCallback((product) => {
    const classification = getProductClassificationState(product);
    return classification.category?.name || product?.category_name || product?.category || '-';
  }, [getProductClassificationState]);

  const getProductPosDescriptionPreview = useCallback((product) => {
    const classification = getProductClassificationState(product);
    return buildPosDescriptionPreview({
      ...product,
      category: classification.categoryPrefix || product?.category,
      specs: product?.technical_specs || product?.specs || {},
      pos_description_fields: product?.pos_description_fields || [],
      pos_description_use_override: Boolean(product?.pos_description_use_override),
      catalog_pos_description_enabled: product?.catalog_pos_description_enabled,
      catalog_pos_description_fields: product?.catalog_pos_description_fields || [],
    }, classification.categoryPrefix || product?.category);
  }, [getProductClassificationState]);

  const getDisplayedStockForProduct = useCallback((product) => {
    if (!locationFilter || locationFilter === 'all') {
      return Number(product?.current_stock || 0);
    }

    const locationSnapshot = locationAvailabilityMap[product?.id];
    if (!locationSnapshot) return 0;
    return Number(locationSnapshot.selected_location_available || 0);
  }, [locationAvailabilityMap, locationFilter]);

  const getReservedCountForProduct = useCallback((product) => {
    if (locationFilter && locationFilter !== 'all') {
      const availabilityRows = locationAvailabilityMap[product?.id]?.availability_by_location || [];
      const selectedRow = availabilityRows.find((row) => row.location_id === locationFilter || row.is_current);
      return Number(selectedRow?.reserved || 0);
    }

    if (product?.item_state_counts?.reserved != null) {
      return Number(product.item_state_counts.reserved || 0);
    }

    if (product?.reserved_by_location && typeof product.reserved_by_location === 'object') {
      return Object.values(product.reserved_by_location).reduce((sum, value) => sum + Number(value || 0), 0);
    }

    return 0;
  }, [locationAvailabilityMap, locationFilter]);

  const matchesStateFilter = useCallback((product, filterValue) => {
    if (!filterValue || filterValue === 'all') return true;

    const normalizedFilter = filterValue === 'in_repair' ? 'repair' : filterValue;
    const stateCounts = product?.item_state_counts || {};

    if (normalizedFilter === 'in_stock') {
      return getDisplayedStockForProduct(product) > 0 || Number(stateCounts.in_stock || 0) > 0;
    }

    if (normalizedFilter === 'reserved') {
      return (stateFilteredProductIds?.has(product?.id) || false) || getReservedCountForProduct(product) > 0;
    }

    if (normalizedFilter === 'sold') {
      return (stateFilteredProductIds?.has(product?.id) || false) || Number(stateCounts.sold || 0) > 0;
    }

    if (normalizedFilter === 'repair') {
      return (stateFilteredProductIds?.has(product?.id) || false) || Number(stateCounts.repair || 0) > 0;
    }

    return Number(stateCounts[normalizedFilter] || 0) > 0;
  }, [getDisplayedStockForProduct, getReservedCountForProduct, stateFilteredProductIds]);

  const inventoryStateChips = useMemo(() => {
    return INVENTORY_STATE_OPTIONS
      .filter((option) => option.value !== 'all')
      .map((option) => ({
        ...option,
        count: products.filter((product) => matchesStateFilter(product, option.value)).length,
      }))
      .filter((option) => option.count > 0);
  }, [products, matchesStateFilter]);

  const getStockStatusMeta = useCallback((stockValue, reorderLevel = 0) => {
    if (stockValue <= 0) {
      return {
        label: 'Sem stock',
        badgeClass: 'border-red-200 bg-red-50 text-red-700',
        valueClass: 'text-red-600',
      };
    }

    if (stockValue <= reorderLevel) {
      return {
        label: 'Stock baixo',
        badgeClass: 'border-amber-200 bg-amber-50 text-amber-700',
        valueClass: 'text-amber-600',
      };
    }

    return {
      label: 'Disponível',
      badgeClass: 'border-emerald-200 bg-emerald-50 text-emerald-700',
      valueClass: 'text-slate-900',
    };
  }, []);
  
  // Filter products based on all criteria
  const filteredProducts = useMemo(() => {
    let result = products;
    
    // Filter by tab (category)
    if (activeTab && activeTab !== 'all') {
      result = result.filter((product) => getCanonicalCategoryTab(getCategoryPrefix(product)) === activeTab);
    }
    
    // Filter by subcategory (if selected)
    if (subcategoryFilter && subcategoryFilter !== 'all') {
      const subcategory = productSubcategories.find(s => s.id === subcategoryFilter);
      if (subcategory) {
        result = result.filter((product) => product.sku?.toUpperCase()?.startsWith(subcategory.prefix));
      }
    }
    
    // Filter by search query
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      result = result.filter(p => 
        p.name?.toLowerCase().includes(query) ||
        p.sku?.toLowerCase().includes(query) ||
        p.barcode?.toLowerCase().includes(query) ||
        p.brand?.toLowerCase().includes(query) ||
        p.model?.toLowerCase().includes(query) ||
        p.part_number?.toLowerCase().includes(query)
      );
    }
    
    // Filter by trackable
    if (trackableFilter && trackableFilter !== 'all') {
      const isTrackable = trackableFilter === 'true';
      result = result.filter(p => p.is_trackable === isTrackable);
    }

    // Filter by item/product state
    if (stateFilter && stateFilter !== 'all') {
      result = result.filter((product) => matchesStateFilter(product, stateFilter));
    }
    
    // Filter by location (products that have items in selected location)
    if (locationFilter && locationFilter !== 'all') {
      if (stateFilter === 'all' || stateFilter === 'in_stock') {
        result = result.filter((product) => getDisplayedStockForProduct(product) > 0);
      } else {
        result = result.filter((product) => matchesStateFilter(product, stateFilter));
      }
    }
    
    // Filter by deleted (admin only)
    if (!showDeleted) {
      result = result.filter(p => !p.is_deleted);
    }

    return result;
  }, [products, activeTab, subcategoryFilter, searchQuery, trackableFilter, stateFilter, showDeleted, locationFilter, getCategoryPrefix, productSubcategories, getDisplayedStockForProduct, matchesStateFilter]);

  const selectedProducts = useMemo(
    () => products.filter((product) => selectedProductIds.includes(product.id)),
    [products, selectedProductIds]
  );

  const effectiveBulkUpdateIds = useMemo(() => {
    if (bulkUpdateUseSelection && selectedProductIds.length > 0) {
      return selectedProductIds;
    }
    return [];
  }, [bulkUpdateUseSelection, selectedProductIds]);

  const bulkUpdateScopeDescription = useMemo(() => {
    if (bulkUpdateUseSelection && selectedProducts.length > 0) {
      return `${selectedProducts.length} produto(s) seleccionados`;
    }

    const scopeLabel = activeTab === 'all'
      ? 'Visão global'
      : CATEGORY_LABELS[activeTab]?.full || productCategories.find((category) => getCanonicalCategoryTab(category.base_prefix) === activeTab)?.name || activeTab;
    const scopeBits = [scopeLabel];
    if (subcategoryFilter !== 'all') {
      const subcategory = productSubcategories.find((item) => item.id === subcategoryFilter);
      if (subcategory?.name) {
        scopeBits.push(subcategory.name);
      }
    }
    scopeBits.push(`${filteredProducts.length} produto(s) no filtro actual`);
    return scopeBits.filter(Boolean).join(' • ');
  }, [activeTab, bulkUpdateUseSelection, filteredProducts.length, productCategories, productSubcategories, selectedProducts.length, subcategoryFilter]);

  const sortedProducts = useMemo(() => {
    const directionFactor = inventorySortDirection === 'asc' ? 1 : -1;

    const getComparableValue = (product) => {
      switch (inventorySortField) {
        case 'product':
          return String(product.name || '');
        case 'code':
          return String(product.sku || '');
        case 'classification':
          return `${getProductCategoryLabel(product)} ${getProductSubcategoryLabel(product) || ''}`.trim();
        case 'summary':
          return String(
            getProductPosDescriptionPreview(product)
            || [
              product?.part_number,
              product?.color,
              product?.technical_specs?.resumo,
              product?.specs?.resumo,
              product?.description,
            ].filter(Boolean).join(' • ')
            || 'Sem resumo técnico'
          );
        case 'price':
          return Number(product.unit_price || 0);
        case 'stock':
        default:
          return Number(getDisplayedStockForProduct(product) || 0);
      }
    };

    return [...filteredProducts].sort((left, right) => {
      const leftValue = getComparableValue(left);
      const rightValue = getComparableValue(right);

      if (typeof leftValue === 'number' && typeof rightValue === 'number') {
        return (leftValue - rightValue) * directionFactor;
      }

      return String(leftValue || '').localeCompare(String(rightValue || ''), 'pt', { sensitivity: 'base' }) * directionFactor;
    });
  }, [filteredProducts, getDisplayedStockForProduct, getProductCategoryLabel, getProductPosDescriptionPreview, getProductSubcategoryLabel, inventorySortDirection, inventorySortField]);

  const paginatedInventoryProducts = useMemo(
    () => paginateItems(sortedProducts, inventoryPage, DEFAULT_TABLE_PAGE_SIZE),
    [inventoryPage, sortedProducts]
  );

  useEffect(() => {
    setInventoryPage((current) => Math.min(current, paginatedInventoryProducts.totalPages));
  }, [paginatedInventoryProducts.totalPages]);

  useEffect(() => {
    setInventoryPage(1);
  }, [activeTab, subcategoryFilter, searchQuery, locationFilter, stateFilter, trackableFilter, showDeleted, inventorySortField, inventorySortDirection]);

  const inventorySortLabel = useMemo(
    () => INVENTORY_SORT_OPTIONS.find((option) => option.value === inventorySortField)?.label || 'Stock',
    [inventorySortField],
  );

  const handleInventorySort = useCallback((field) => {
    if (inventorySortField === field) {
      setInventorySortDirection((current) => current === 'asc' ? 'desc' : 'asc');
      return;
    }

    setInventorySortField(field);
    setInventorySortDirection(field === 'stock' || field === 'price' ? 'desc' : 'asc');
  }, [inventorySortField]);

  const renderInventorySortIndicator = useCallback((field) => {
    if (inventorySortField !== field) {
      return <ChevronDown className="h-3.5 w-3.5 text-slate-300" />;
    }

    return inventorySortDirection === 'asc'
      ? <ChevronUp className="h-3.5 w-3.5 text-slate-700" />
      : <ChevronDown className="h-3.5 w-3.5 text-slate-700" />;
  }, [inventorySortDirection, inventorySortField]);
  const hasInventoryData = Boolean(
    products.length ||
    deletedProducts.length ||
    categories.length ||
    productCategories.length ||
    productSubcategories.length ||
    locations.length
  );

  useEffect(() => {
    setSelectedProductIds([]);
  }, [activeTab, subcategoryFilter, searchQuery, trackableFilter, stateFilter, showDeleted, locationFilter]);
  
  // Get quantity + product counters per category for tab badges
  const categoryQuantifiers = useMemo(() => {
    const counts = {};
    products.forEach((product) => {
      const prefix = getCanonicalCategoryTab(getCategoryPrefix(product));
      const currentStock = Math.max(Number(getDisplayedStockForProduct(product) || 0), 0);
      counts[prefix] = counts[prefix] || { quantity: 0, products: 0 };
      counts[prefix].quantity += currentStock;
      counts[prefix].products += 1;
    });
    return counts;
  }, [products, getCategoryPrefix, getDisplayedStockForProduct]);

  const totalInventoryQuantifier = useMemo(() => {
    return products.reduce((sum, product) => sum + Math.max(Number(getDisplayedStockForProduct(product) || 0), 0), 0);
  }, [products, getDisplayedStockForProduct]);

  const filteredInventoryQuantifier = useMemo(() => {
    return filteredProducts.reduce((sum, product) => sum + Math.max(Number(getDisplayedStockForProduct(product) || 0), 0), 0);
  }, [filteredProducts, getDisplayedStockForProduct]);

  const filteredLowStockCount = useMemo(() => {
    return filteredProducts.filter((product) => {
      const displayedStock = Number(getDisplayedStockForProduct(product) || 0);
      return displayedStock <= Number(product.reorder_level || 0);
    }).length;
  }, [filteredProducts, getDisplayedStockForProduct]);

  const activeFiltersCount = useMemo(() => {
    return [
      Boolean(searchQuery),
      locationFilter !== 'all',
      stateFilter !== 'all',
      trackableFilter !== 'all',
      subcategoryFilter !== 'all',
      showDeleted,
    ].filter(Boolean).length;
  }, [searchQuery, locationFilter, stateFilter, trackableFilter, subcategoryFilter, showDeleted]);

  const selectedLocationLabel = useMemo(() => {
    if (locationFilter === 'all') return 'Todas localizações';
    return locations.find((location) => location.id === locationFilter)?.name || 'Localização seleccionada';
  }, [locationFilter, locations]);

  const getProductStockByLocationSnapshot = useCallback((product) => {
    if (!product) return {};

    const availabilityRows = locationAvailabilityMap[product.id]?.availability_by_location || [];
    if (availabilityRows.length > 0) {
      return availabilityRows.reduce((accumulator, row) => {
        const locationName = row.location_name || locations.find((loc) => loc.id === row.location_id)?.name || 'Localização';
        accumulator[locationName] = {
          count: Number(row.on_hand || 0),
          available: Number(row.available || 0),
          locationId: row.location_id,
        };
        return accumulator;
      }, {});
    }

    if (product.is_trackable && selectedProduct?.id === product.id && stockByLocation && Object.keys(stockByLocation).length > 0) {
      return stockByLocation;
    }

    if (product.stock_by_location && typeof product.stock_by_location === 'object') {
      return Object.entries(product.stock_by_location).reduce((accumulator, [locationId, quantity]) => {
        const locationName = locations.find((loc) => loc.id === locationId)?.name || locationId;
        accumulator[locationName] = {
          count: Number(quantity || 0),
          available: Number(quantity || 0),
          locationId,
        };
        return accumulator;
      }, {});
    }

    if (product.location_id && Number(product.current_stock || 0) > 0) {
      const locationName = locations.find((loc) => loc.id === product.location_id)?.name || product.location_id;
      return {
        [locationName]: {
          count: Number(product.current_stock || 0),
          available: Number(product.current_stock || 0),
          locationId: product.location_id,
        }
      };
    }

    return {};
  }, [locationAvailabilityMap, locations, selectedProduct?.id, stockByLocation]);

  const getProductStockDistributionSummary = useCallback((product) => {
    const stockSnapshot = getProductStockByLocationSnapshot(product);
    const entries = Object.entries(stockSnapshot)
      .map(([locationName, data]) => ({
        locationName,
        quantity: Number(data?.available ?? data?.count ?? 0),
      }))
      .filter((entry) => entry.quantity > 0)
      .sort((left, right) => right.quantity - left.quantity || left.locationName.localeCompare(right.locationName, 'pt', { sensitivity: 'base' }));

    if (entries.length === 0) {
      return Number(product?.current_stock || 0) > 0 ? 'Stock sem localização definida' : 'Sem stock';
    }

    const visibleEntries = entries.slice(0, 2).map((entry) => `${entry.locationName} (${entry.quantity})`);
    const remainingCount = entries.length - visibleEntries.length;
    return remainingCount > 0 ? `${visibleEntries.join(' • ')} +${remainingCount}` : visibleEntries.join(' • ');
  }, [getProductStockByLocationSnapshot]);

  const getProductStockScopeLabel = useCallback((product) => {
    if (locationFilter !== 'all') {
      return selectedLocationLabel;
    }

    const stockSnapshot = getProductStockByLocationSnapshot(product);
    const locationNames = Object.keys(stockSnapshot);
    if (locationNames.length === 1) {
      return locationNames[0];
    }
    if (locationNames.length > 1) {
      return `${locationNames.length} localizações activas`;
    }
    return Number(product?.current_stock || 0) > 0 ? 'Sem localização definida' : 'Sem stock';
  }, [getProductStockByLocationSnapshot, locationFilter, selectedLocationLabel]);

  const activeCategoryLabel = useMemo(() => {
    if (activeTab === 'all') return 'Visão global';
    return CATEGORY_LABELS[activeTab]?.full || productCategories.find((category) => getCanonicalCategoryTab(category.base_prefix) === activeTab)?.name || activeTab;
  }, [activeTab, productCategories]);

  const getCompactSpecText = useCallback((product) => {
    const preview = getProductPosDescriptionPreview(product);
    if (preview) return preview;

    const specs = product?.technical_specs || product?.specs || {};
    const fallback = [
      buildProductHighlightSummary(product),
      product?.part_number,
      specs?.resumo,
      product?.description,
    ].filter(Boolean);

    return fallback.join(' • ') || 'Sem resumo técnico';
  }, [getProductPosDescriptionPreview]);

  const getDefaultStockAdjustmentLocationId = useCallback((product) => {
    if (locationFilter && locationFilter !== 'all') {
      return locationFilter;
    }

    if (product?.stock_by_location && typeof product.stock_by_location === 'object') {
      const firstPositiveLocation = Object.entries(product.stock_by_location)
        .find(([, quantity]) => Number(quantity || 0) > 0)?.[0];
      if (firstPositiveLocation) {
        return firstPositiveLocation;
      }
    }

    if (product?.location_id) {
      return product.location_id;
    }

    return locations.find((loc) => loc.type === 'warehouse' || loc.type === 'store')?.id || locations[0]?.id || '';
  }, [locationFilter, locations]);

  // Clear all filters
  const handleClearFilters = useCallback(() => {
    setSearchQuery('');
    setLocationFilter('all');
    setStateFilter('all');
    setTrackableFilter('all');
    setShowDeleted(false);
    setSubcategoryFilter('all');
  }, []);

  // ==========================================================================
  // PRODUCT ACTIONS
  // ==========================================================================

  // Find similar products for autocomplete
  const findSimilarProducts = useMemo(() => {
    const nameTerm = (formData.name || '').trim().toLowerCase();
    const skuTerm = (formData.sku || '').trim().toLowerCase();
    const barcodeTerm = (formData.barcode || '').trim().toLowerCase();
    const brandTerm = (formData.brand || '').trim().toLowerCase();
    const modelTerm = (formData.model || '').trim().toLowerCase();

    if ([nameTerm, skuTerm, barcodeTerm, brandTerm, modelTerm].every((term) => term.length < 2)) {
      return [];
    }

    const nameTokens = nameTerm.split(/\s+/).filter((token) => token.length >= 2);

    return products
      .map((product) => {
        const skuValue = (product.sku || '').toLowerCase();
        const barcodeValue = (product.barcode || '').toLowerCase();
        const nameValue = (product.name || '').toLowerCase();
        const brandValue = (product.brand || '').toLowerCase();
        const modelValue = (product.model || '').toLowerCase();
        const haystack = `${nameValue} ${brandValue} ${modelValue} ${skuValue} ${barcodeValue} ${(product.part_number || '').toLowerCase()}`;

        let score = 0;

        if (skuTerm && skuValue === skuTerm) score += 120;
        else if (skuTerm && skuValue.includes(skuTerm)) score += 70;

        if (barcodeTerm && barcodeValue === barcodeTerm) score += 120;
        else if (barcodeTerm && barcodeValue.includes(barcodeTerm)) score += 70;

        if (nameTerm && nameValue === nameTerm) score += 60;

        if (nameTokens.length > 0) {
          score += nameTokens.reduce((total, token) => total + (haystack.includes(token) ? 15 : 0), 0);
        }

        if (brandTerm && brandValue.includes(brandTerm)) score += 20;
        if (modelTerm && modelValue.includes(modelTerm)) score += 20;

        return { product, score };
      })
      .filter((entry) => entry.score >= 25)
      .sort((a, b) => b.score - a.score)
      .slice(0, 6)
      .map((entry) => entry.product);
  }, [formData.barcode, formData.brand, formData.model, formData.name, formData.sku, products]);

  // Check for potential duplicates when name or SKU changes
  useEffect(() => {
    if (formData.name.length >= 3 || formData.sku.length >= 3 || formData.barcode.length >= 3) {
      const similar = findSimilarProducts;
      const normalizedSku = (formData.sku || '').toLowerCase();
      const normalizedBarcode = (formData.barcode || '').toLowerCase();
      const exactSkuMatch = products.find((p) => (p?.sku || '').toLowerCase() === normalizedSku);
      const exactBarcodeMatch = normalizedBarcode
        ? products.find((p) => (p?.barcode || '').toLowerCase() === normalizedBarcode)
        : null;
      
      if (exactSkuMatch) {
        setSimilarProducts([exactSkuMatch]);
        setShowDuplicateWarning(true);
      } else if (exactBarcodeMatch) {
        setSimilarProducts([exactBarcodeMatch]);
        setShowDuplicateWarning(true);
      } else if (similar.length > 0) {
        setSimilarProducts(similar);
        setShowDuplicateWarning(true);
      } else {
        setSimilarProducts([]);
        setShowDuplicateWarning(false);
      }
    } else {
      setShowDuplicateWarning(false);
    }
  }, [formData.barcode, formData.name, formData.sku, findSimilarProducts, products]);

  const formatAuditValue = (value) => {
    if (value === null || value === undefined || value === '') return '-';
    if (typeof value === 'object') {
      try {
        return JSON.stringify(value);
      } catch {
        return String(value);
      }
    }
    return String(value);
  };

  // Apply existing product data
  const applyExistingProduct = (product) => {
    const classification = getProductClassificationState(product);
    setSkuManuallyEdited(true);
    setFormData({
      ...initialFormData,
      name: product.name,
      sku: `${product.sku || product.name || 'SKU'}-NEW`,
      barcode: '',
      description: product.description || '',
      brand: product.brand || '',
      model: product.model || '',
      part_number: product.part_number || '',
      category: classification.categoryPrefix,
      subcategory: classification.subcategoryId,
      invalidSubcategory: classification.invalidSubcategory,
      ram: product.ram || '',
      rom: product.rom || product.storage || '',
      color: product.color || '',
      power: product.power || '',
      warranty: product.warranty || '',
      connector: product.connector || '',
      year: product.year?.toString() || '',
      unit_price: product.unit_price?.toString() || '',
      cost_price: product.cost_price?.toString() || '',
      is_trackable: product.is_trackable,
      reorder_level: product.reorder_level?.toString() || '10',
      specs: product.technical_specs || product.specs || {},
      pos_description_use_override: Boolean(product.pos_description_use_override),
      pos_description_fields: product.pos_description_fields || [],
      variant_parent_product_id: product.id || '',
      variant_mode: 'new_variant',
    });
    setNameSearchOpen(false);
    toast.info('Dados do produto carregados. Ajuste o SKU para criar uma nova variante.');
  };

  // Edit functionality
  const handleOpenEdit = (product) => {
    if (!canEdit) {
      toast.error('Sem permissão para editar produtos');
      return;
    }
    const classification = getProductClassificationState(product);
    setSelectedProduct(product);
    setEditFormData({
      name: product.name,
      barcode: product.barcode || '',
      description: product.description || '',
      brand: product.brand || '',
      model: product.model || '',
      part_number: product.part_number || '',
      color: product.color || '',
      power: product.power || '',
      warranty: product.warranty || '',
      connector: product.connector || '',
      category: classification.categoryPrefix,
      subcategory: classification.subcategoryId,
      invalidSubcategory: classification.invalidSubcategory,
      unit_price: product.unit_price?.toString() || '',
      cost_price: product.cost_price?.toString() || '',
      reorder_level: product.reorder_level?.toString() || '10',
      specs: product.technical_specs || product.specs || {},
      pos_description_use_override: Boolean(product.pos_description_use_override),
      pos_description_fields: product.pos_description_fields || [],
    });
    setEditJustification('');
    setEditDialogOpen(true);
  };

  const handleSaveEdit = () => {
    if (!editJustification || editJustification.trim().length < 5) {
      toast.error('Justificação obrigatória (mínimo 5 caracteres)');
      return;
    }

    const availableSubcategories = getSubcategoriesForCategory(editFormData?.category);
    if (editFormData?.invalidSubcategory && availableSubcategories.length > 0 && !editFormData?.subcategory) {
      toast.error('Seleccione uma subcategoria válida antes de guardar');
      return;
    }

    setConfirmEditDialogOpen(true);
  };

  const handleConfirmEdit = async () => {
    if (!selectedProduct?.id || !editFormData) {
      toast.error('Produto inválido para edição');
      return;
    }

    try {
      await inventoryAPI.editProduct(selectedProduct.id, {
        ...editFormData,
        ...buildClassificationPayload(editFormData),
        ...buildProductHighlightPayload(editFormData),
        technical_specs: editFormData.specs,
        barcode: editFormData.barcode?.trim() || null,
        unit_price: editFormData.unit_price ? parseFloat(editFormData.unit_price) : undefined,
        cost_price: editFormData.cost_price ? parseFloat(editFormData.cost_price) : undefined,
        reorder_level: editFormData.reorder_level ? parseInt(editFormData.reorder_level) : undefined,
        invalidSubcategory: undefined,
      }, editJustification.trim());
      
      toast.success('Produto actualizado! Alteração registada no histórico.');
      setConfirmEditDialogOpen(false);
      setEditDialogOpen(false);
      setEditJustification('');
      loadProducts();
    } catch (error) {
      const detail = error?.response?.data?.detail;
      if (detail?.code === 'variant_required') {
        const attributes = (detail.required_variant_attributes || []).map((item) => item.label).join(', ');
        toast.error(`Esta alteração exige nova variante/SKU: ${attributes || 'atributos principais'}`);
        return;
      }
      toast.error(getErrorMessage(error, 'Erro ao actualizar produto'));
    }
  };

  // Delete functionality
  const handleOpenDelete = (product) => {
    if (!canEdit) {
      toast.error('Sem permissão para eliminar produtos');
      return;
    }

    setSelectedProduct(product);
    setDeleteReason('');
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!selectedProduct?.id) {
      toast.error('Produto inválido para eliminação');
      return;
    }

    if (!deleteReason || deleteReason.trim().length < 5) {
      toast.error('Motivo de eliminação obrigatório (mínimo 5 caracteres)');
      return;
    }
    
    try {
      await inventoryAPI.deleteProduct(selectedProduct.id, deleteReason.trim());
      toast.success('Produto eliminado com sucesso!');
      setDeleteDialogOpen(false);
      setDeleteReason('');
      setSelectedProduct(null);
      await refreshInventoryCollections();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Erro ao eliminar produto'));
    }
  };

  // Restore functionality (Admin only)
  const handleRestoreProduct = async (product) => {
    try {
      await inventoryAPI.restoreProduct(product.id);
      toast.success('Produto restaurado com sucesso!');
      await refreshInventoryCollections();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Erro ao restaurar produto'));
    }
  };

  const toggleProductSelection = (productId, checked) => {
    setSelectedProductIds((prev) => {
      if (checked) {
        return prev.includes(productId) ? prev : [...prev, productId];
      }
      return prev.filter((id) => id !== productId);
    });
  };

  const allProductsSelected = paginatedInventoryProducts.pageItems.length > 0 && paginatedInventoryProducts.pageItems.every((product) => selectedProductIds.includes(product.id));

  useEffect(() => {
    if (typeof window === 'undefined') return;
    writeInventoryColumnWidths(inventoryColumnWidths);
  }, [inventoryColumnWidths]);

  const startInventoryColumnResize = useCallback((index, event) => {
    showResizeGuide(event.clientX);
    const wrapperWidth = inventoryTableWrapperRef.current?.offsetWidth || 1;
    inventoryColumnResizeRef.current = {
      index,
      startX: event.clientX,
      startWidths: [...inventoryColumnWidths],
      wrapperWidth,
    };

    const onMouseMove = (moveEvent) => {
      const state = inventoryColumnResizeRef.current;
      if (!state) return;
      showResizeGuide(moveEvent.clientX);
      const deltaPercent = ((moveEvent.clientX - state.startX) / state.wrapperWidth) * 100;
      const next = [...state.startWidths];
      next[index] = Math.max(MIN_INVENTORY_COLUMN_WIDTHS[index], state.startWidths[index] + deltaPercent);
      next[index + 1] = Math.max(MIN_INVENTORY_COLUMN_WIDTHS[index + 1], state.startWidths[index + 1] - deltaPercent);
      setInventoryColumnWidths(next);
    };

    const onMouseUp = () => {
      inventoryColumnResizeRef.current = null;
      hideResizeGuide();
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', onMouseUp);
    };

    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', onMouseUp);
  }, [inventoryColumnWidths]);

  const toggleSelectAllProducts = (checked) => {
    if (checked) {
      setSelectedProductIds((current) => Array.from(new Set([...current, ...paginatedInventoryProducts.pageItems.map((product) => product.id)])));
      return;
    }
    setSelectedProductIds((current) => current.filter((id) => !paginatedInventoryProducts.pageItems.some((product) => product.id === id)));
  };

  const handleBulkProductStatus = async (action) => {
    if (!selectedProductIds.length) {
      toast.error('Seleccione produtos para ação em massa');
      return;
    }
    try {
      const response = await inventoryAPI.bulkStatus({
        ids: selectedProductIds,
        action,
        reason: `Ação em massa: ${action}`,
      });
      toast.success(response.data.message || 'Ação em massa concluída');
      setSelectedProductIds([]);
      await refreshInventoryCollections();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Erro na ação em massa'));
    }
  };

  const handleExportSelectedProducts = () => {
    if (!selectedProductIds.length) {
      toast.error('Seleccione produtos para exportar');
      return;
    }
    const selected = products.filter((product) => selectedProductIds.includes(product.id));
    if (!selected.length) {
      toast.error('Nenhum produto seleccionado para exportação');
      return;
    }

    const headers = ['SKU', 'Nome', 'Categoria', 'Marca', 'Modelo', 'Stock', 'Preço Venda', 'Estado'];
    const rows = selected.map((product) => [
      product.sku || '',
      product.name || '',
      getProductCategoryLabel(product),
      product.brand || '',
      product.model || '',
      getDisplayedStockForProduct(product),
      product.unit_price || 0,
      product.is_deleted ? 'Eliminado' : (product.is_active ? 'Activo' : 'Inactivo'),
    ]);

    const csv = [headers, ...rows]
      .map((row) => row.map((field) => `"${String(field).replace(/"/g, '""')}"`).join(';'))
      .join('\n');

    const blob = new Blob([`\uFEFF${csv}`], { type: 'text/csv;charset=utf-8;' });
    const href = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = href;
    link.download = `produtos_seleccionados_${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(href);
    toast.success(`${selected.length} produtos exportados`);
  };

  const downloadBlobFile = (blob, filename) => {
    const href = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = href;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(href);
  };

  const handleDownloadBulkUpdateTemplate = async () => {
    try {
      setBulkUpdateDownloading(true);
      const response = await inventoryAPI.downloadBulkUpdateTemplate({
        ids: effectiveBulkUpdateIds,
        category_id: activeTab !== 'all' ? (productCategories.find((category) => getCanonicalCategoryTab(category.base_prefix) === activeTab)?.id || undefined) : undefined,
        subcategory_id: subcategoryFilter !== 'all' ? subcategoryFilter : undefined,
      });

      const filename = `inventory_bulk_update_${new Date().toISOString().slice(0, 10)}.xlsx`;
      downloadBlobFile(response.data, filename);
      toast.success('Template de actualização descarregado');
    } catch (error) {
      toast.error(getErrorMessage(error, 'Erro ao descarregar template de actualização'));
    } finally {
      setBulkUpdateDownloading(false);
    }
  };

  const handleExecuteBulkUpdate = async () => {
    if (!bulkUpdateFile) {
      toast.error('Seleccione primeiro o ficheiro Excel de actualização');
      return;
    }

    const formData = new FormData();
    formData.append('file', bulkUpdateFile);

    try {
      setBulkUpdateUploading(true);
      const response = await inventoryAPI.importBulkUpdateFile(formData);
      const summary = response.data?.summary || null;
      setBulkUpdateSummary({
        ...summary,
        results: response.data?.results || [],
        location_summary: response.data?.location_summary || [],
        preview_only: Boolean(response.data?.preview_only),
      });
      await refreshInventoryCollections();
      toast.success(response.data?.message || 'Actualização em massa concluída');
      if ((summary?.errors || 0) === 0) {
        setBulkUpdateFile(null);
      }
    } catch (error) {
      toast.error(getErrorMessage(error, 'Erro ao aplicar actualização em massa'));
    } finally {
      setBulkUpdateUploading(false);
    }
  };

  const handlePreviewBulkUpdate = async () => {
    if (!bulkUpdateFile) {
      toast.error('Seleccione primeiro o ficheiro Excel de actualização');
      return;
    }

    const formData = new FormData();
    formData.append('file', bulkUpdateFile);

    try {
      setBulkUpdatePreviewing(true);
      const response = await inventoryAPI.importBulkUpdateFile(formData, true);
      const summary = response.data?.summary || null;
      setBulkUpdateSummary({
        ...summary,
        results: response.data?.results || [],
        location_summary: response.data?.location_summary || [],
        preview_only: Boolean(response.data?.preview_only),
      });
      toast.success(response.data?.message || 'Pré-validação concluída');
    } catch (error) {
      toast.error(getErrorMessage(error, 'Erro ao pré-validar actualização em massa'));
    } finally {
      setBulkUpdatePreviewing(false);
    }
  };

  const openBulkDeleteDialog = () => {
    if (!selectedProductIds.length) {
      toast.error('Seleccione produtos para eliminar em massa');
      return;
    }
    setBulkDeleteReason('');
    setBulkDeleteHardDelete(false);
    setBulkDeleteDialogOpen(true);
  };

  const handleConfirmBulkDelete = async () => {
    if (!selectedProductIds.length) {
      toast.error('Seleccione produtos para eliminar em massa');
      return;
    }

    try {
      setBulkDeleteLoading(true);
      const response = await inventoryAPI.bulkDelete({
        ids: selectedProductIds,
        reason: bulkDeleteReason,
        hard_delete: bulkDeleteHardDelete,
      });

      const skipped = response.data?.skipped || [];
      const softDeleted = Number(response.data?.soft_deleted || 0);
      const hardDeleted = Number(response.data?.hard_deleted || 0);
      const summary = [
        softDeleted > 0 ? `${softDeleted} soft delete` : null,
        hardDeleted > 0 ? `${hardDeleted} hard delete` : null,
        skipped.length > 0 ? `${skipped.length} ignorado(s)` : null,
      ].filter(Boolean).join(' • ');

      toast.success(summary ? `Operação concluída: ${summary}` : 'Operação concluída');
      setBulkDeleteDialogOpen(false);
      setSelectedProductIds([]);
      await refreshInventoryCollections();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Erro ao eliminar produtos em massa'));
    } finally {
      setBulkDeleteLoading(false);
    }
  };

  // Audit log functionality
  const handleViewAudit = async (product) => {
    setSelectedProduct(product);
    try {
      const response = await inventoryAPI.getProductAudit(product.id);
      setAuditLogs(response.data);
      setAuditDialogOpen(true);
    } catch (error) {
      toast.error('Erro ao carregar histórico');
    }
  };

  const loadProductItems = async (product) => {
    try {
      const response = await itemsAPI.list({ product_id: product.id });
      const items = response.data || [];
      setProductItems(items);
      
      // Calculate stock by location
      const stockMap = {};
      items.forEach((item) => {
        if (item.state === 'in_stock') {
          const locName = item.location_name || item.location || 'Sem localização';
          if (!stockMap[locName]) {
            stockMap[locName] = { count: 0, items: [], locationType: item.location_type };
          }
          stockMap[locName].count++;
          stockMap[locName].items.push(item);
        }
      });
      setStockByLocation(stockMap);
    } catch (error) {
      toast.error('Erro ao carregar items');
    }
  };

  const buildProductDetailPath = useCallback((product, mode = 'view') => {
    const routeReference = encodeURIComponent(String(product?.sku || product?.id || '').trim());
    return `/inventory/products/${routeReference}${mode === 'edit' ? '?mode=edit' : ''}`;
  }, []);

  const handleViewProduct = async (product, mode = 'view') => {
    navigate(buildProductDetailPath(product, mode), {
      state: {
        productPreview: product,
        sourceMode: mode,
      },
    });
  };

  const handleTransferProduct = (product) => {
    const params = new URLSearchParams({
      product_id: product.id,
      sku: product.sku,
      product_name: product.name,
    });

    const inferredOriginLocationId = locationFilter !== 'all'
      ? locationFilter
      : (product.location_id
        || Object.entries(product.stock_by_location || {}).find(([, quantity]) => Number(quantity || 0) > 0)?.[0]
        || '');

    if (inferredOriginLocationId) {
      params.set('origin_location_id', inferredOriginLocationId);
    }

    navigate(`/stock-transfers?${params.toString()}`);
  };

  const openAddItemDialog = (product) => {
    setSelectedProduct(product);
    const defaultLocation = locations.find(loc => loc.type === 'warehouse' || loc.type === 'store');
    setItemFormData({
      ...initialItemData,
      product_id: product.id,
      purchase_price: product.cost_price?.toString() || '',
      location_id: defaultLocation?.id || ''
    });
    setItemDialogOpen(true);
  };

  const openStockAdjustmentDialog = (product) => {
    setStockAdjustmentProduct(product);
    setStockAdjustmentForm({
      quantity: '1',
      reason: `Reposição manual do SKU ${product.sku}`,
      location_id: getDefaultStockAdjustmentLocationId(product),
    });
    setStockAdjustmentDialogOpen(true);
  };

  const openAddMoreDialog = (product) => {
    if (product.is_trackable) {
      openAddItemDialog(product);
      return;
    }
    openStockAdjustmentDialog(product);
  };

  const openTrackableStockManager = async (product) => {
    setSelectedProduct(product);
    await loadProductItems(product);
    setSerialNumbersDialogOpen(true);
  };

  const closeStockAdjustmentDialog = () => {
    setStockAdjustmentDialogOpen(false);
    setStockAdjustmentProduct(null);
    setStockAdjustmentForm({ quantity: '1', reason: '', location_id: '' });
  };

  const handleAdjustStock = async (e) => {
    e.preventDefault();

    if (!stockAdjustmentProduct?.id) {
      toast.error('Produto inválido para ajuste de stock');
      return;
    }

    const quantity = parseInt(stockAdjustmentForm.quantity, 10);
    if (!quantity || quantity <= 0) {
      toast.error('Informe uma quantidade válida para adicionar ao stock');
      return;
    }

    const reason = stockAdjustmentForm.reason?.trim();
    if (!reason) {
      toast.error('Indique o motivo do ajuste de stock');
      return;
    }

    if (!stockAdjustmentForm.location_id) {
      toast.error('Seleccione a localização do ajuste');
      return;
    }

    try {
      const response = await inventoryAPI.adjustStock(
        stockAdjustmentProduct.id,
        quantity,
        reason,
        stockAdjustmentForm.location_id,
      );
      await loadProducts();
      setSelectedProduct((prev) => (
        prev?.id === stockAdjustmentProduct.id
          ? {
            ...prev,
            current_stock: response.data.new_stock,
            stock_by_location: response.data.stock_by_location || prev.stock_by_location,
            location_id: response.data.location_id || prev.location_id,
            location_name: response.data.location_name || prev.location_name,
            location: response.data.location_name || prev.location,
          }
          : prev
      ));
      toast.success(`Stock actualizado: ${response.data.location_name} = ${response.data.new_location_stock}`);
      closeStockAdjustmentDialog();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Erro ao ajustar stock'));
    }
  };

  // Handle category change - set default trackable value
  const handleCategoryChange = (categoryPrefix) => {
    // Find in new productCategories (using base_prefix)
    const productCategory = productCategories.find(c => c.base_prefix === categoryPrefix);
    // Fallback to legacy categories if not found
    const legacyCategory = categories.find(c => c.key === categoryPrefix);
    const firstSubcategoryId = getSubcategoriesForCategory(categoryPrefix)[0]?.id || '';
    
    const isTrackable = productCategory?.is_trackable_default ?? legacyCategory?.is_trackable_default ?? true;
    
    setFormData((prev) => ({
      ...prev,
      category: categoryPrefix,
      subcategory: firstSubcategoryId,
      invalidSubcategory: false,
      is_trackable: isTrackable,
      specs: {},
      sku: skuManuallyEdited && prev.sku ? prev.sku : '',
      pos_description_use_override: false,
      pos_description_fields: [],
      color: '',
      power: '',
      warranty: '',
      connector: '',
      variant_parent_product_id: prev.variant_parent_product_id || '',
      variant_mode: prev.variant_mode || 'standalone',
    }));
    setSpecsErrors([]);
  };

  const handleEditCategoryChange = (categoryPrefix) => {
    const firstSubcategoryId = getSubcategoriesForCategory(categoryPrefix)[0]?.id || '';
    setEditFormData((prev) => ({
      ...prev,
      category: categoryPrefix,
      subcategory: firstSubcategoryId,
      invalidSubcategory: false,
      pos_description_use_override: false,
      pos_description_fields: [],
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setCreateProductError('');
    
    // Validate specs if category has schema
    if (currentSchema) {
      try {
        const validation = await categoriesAPI.validateSpecs(formData.category, formData.specs, {
          category_id: buildClassificationPayload(formData).category_id,
          product: {
            ...formData,
            ...buildProductHighlightPayload(formData),
          },
        });
        if (!validation.data.valid) {
          setSpecsErrors(validation.data.errors);
          const message = 'Preencha todos os campos obrigatórios da categoria seleccionada';
          setCreateProductError(message);
          toast.error(message);
          return;
        }
      } catch (error) {
        console.error('Validation error:', error);
      }
    }
    
    setSpecsErrors([]);
    
    try {
      const productData = {
        ...formData,
        ...buildClassificationPayload(formData),
        ...buildProductHighlightPayload(formData),
        technical_specs: formData.specs,
        barcode: formData.barcode?.trim() || null,
        year: formData.year?.toString().trim() ? parseInt(formData.year, 10) : undefined,
        unit_price: parseFloat(formData.unit_price),
        cost_price: parseFloat(formData.cost_price),
        reorder_level: parseInt(formData.reorder_level),
        initial_stock: parseInt(formData.initial_stock),
        location_id: formData.location_id || null,
        invalidSubcategory: undefined,
        schema_governance: currentSchema?.governance || null,
        variant_parent_product_id: buildVariantParentProductId(formData) || null,
        // P0.2: Warranty
        warranty_option_id: formData.warranty_option_id || null,
        warranty_days: formData.warranty_days ? parseInt(formData.warranty_days) : null
      };
      
      const response = await inventoryAPI.createProduct(productData);
      toast.success('Produto criado com sucesso!');
      setCreateProductError('');
      setDialogOpen(false);
      setFormData(initialFormData);
      setSkuPreview(null);
      setSkuManuallyEdited(false);
      await loadProducts();
      
      // If trackable, prompt to add items
      if (formData.is_trackable) {
        setSelectedProduct(response.data);
        setItemFormData({
          ...initialItemData,
          product_id: response.data.id,
          purchase_price: formData.cost_price,
          location_id: formData.location_id || response.data.location_id || ''
        });
        setTimeout(() => {
          setItemDialogOpen(true);
        }, 500);
      }
    } catch (error) {
      const message = getErrorMessage(error, 'Erro ao criar produto', {
        translateValidation: true,
        fieldLabels: PRODUCT_CREATE_ERROR_FIELD_LABELS,
      });
      const normalizedSku = (formData.sku || '').toLowerCase();
      const exactSkuMatch = products.find((product) => (product.sku || '').toLowerCase() === normalizedSku);
      const safeMessage = message.toLowerCase().includes('sku já existe') && exactSkuMatch
        ? `${message}. Use a opção "${exactSkuMatch.is_trackable ? 'Registar unidade neste SKU' : 'Adicionar stock neste SKU'}" no aviso acima.`
        : message;
      setCreateProductError(safeMessage);
      toast.error(safeMessage);
    }
  };

  const handleAddItem = async (e) => {
    e.preventDefault();

    if (!selectedProduct?.id) {
      toast.error('Produto inválido para registo de item');
      return;
    }
    
    if (!itemFormData.imei1 && !itemFormData.serial_number) {
      toast.error('É necessário informar pelo menos IMEI1 ou Número de Série');
      return;
    }
    
    if (!itemFormData.location_id) {
      toast.error('É necessário selecionar uma localização');
      return;
    }
    
    try {
      const itemData = {
        ...itemFormData,
        purchase_price: itemFormData.purchase_price ? parseFloat(itemFormData.purchase_price) : null
      };
      
      await itemsAPI.create(itemData);
      toast.success('Item registado com sucesso!');
      
      await loadProducts();
      
      setItemFormData({
        ...initialItemData,
        product_id: selectedProduct.id,
        purchase_price: itemFormData.purchase_price,
        supplier: itemFormData.supplier,
        location_id: itemFormData.location_id,
        condition: itemFormData.condition
      });
      
    } catch (error) {
      toast.error(getErrorMessage(error, 'Erro ao registar item'));
    }
  };

  const closeItemDialog = () => {
    setItemDialogOpen(false);
    setSelectedProduct(null);
    setItemFormData(initialItemData);
  };

  const canAddProduct = hasRole(['admin', 'store_manager']);
  const canViewCost = hasRole(['admin', 'store_manager', 'accountant']);

  // ==========================================================================
  // RENDER
  // ==========================================================================

  // Get ordered categories for tabs
  const orderedCategories = useMemo(() => {
    const catalogPrefixes = safeProductCategories.map((category) => getCanonicalCategoryTab(category.base_prefix));
    const prefixOrder = [...new Set([...TAB_ORDER.map(getCanonicalCategoryTab), ...catalogPrefixes])];

    return prefixOrder.map((prefix) => {
      const cat = productCategories.find((category) => getCanonicalCategoryTab(category.base_prefix) === prefix);
      return cat ? { ...cat, base_prefix: prefix } : { base_prefix: prefix, name: CATEGORY_LABELS[prefix]?.full || prefix };
    }).filter(Boolean);
  }, [productCategories]);

  // Get current category subcategories
  const currentSubcategories = useMemo(() => {
    if (activeTab === 'all') return [];
    const prefixes = getTabCategoryPrefixes(activeTab);
    const relatedCategoryIds = sanitizeCatalogList(safeProductCategories)
      .filter((category) => prefixes.includes(getCanonicalCategoryTab(category.base_prefix)))
      .map((category) => category.id);
    return sanitizeCatalogList(safeProductSubcategories).filter((subcategory) => relatedCategoryIds.includes(subcategory.category_id));
  }, [activeTab, safeProductCategories, safeProductSubcategories]);

  return (
    <div className="space-y-4 bg-slate-50/70 p-3 md:p-4 lg:p-5" data-testid="inventory-page">
      <TooltipProvider delayDuration={200}>
        <ModuleStateBoundary
          loading={loading}
          hasData={hasInventoryData}
          partialIssues={partialIssues}
          loadingTestId="inventory-loading-state"
          partialIssuesTestId="inventory-partial-issues"
          emptyState={(
            <EmptyStatePanel
              title="Inventário sem dados iniciais"
              description="Configure produtos, categorias e localizações base para que o inventário continue estável mesmo com falhas parciais do legado."
              ctaLabel="Abrir Configurações"
              ctaTo="/settings"
              testId="inventory-empty-state"
            />
          )}
        >
        <Tabs value={activeTab} onValueChange={(v) => { setActiveTab(normalizeCategoryTab(v)); setSubcategoryFilter('all'); }} className="w-full space-y-6">
          <div className="rounded-[30px] border border-slate-200 bg-[radial-gradient(circle_at_top_left,_rgba(59,130,246,0.08),_transparent_30%),linear-gradient(180deg,_#ffffff,_#f8fafc)] p-4 shadow-sm md:p-5" data-testid="inventory-header-panel">
            <div className="flex flex-col gap-5">
              <div className="flex flex-col gap-4 xl:flex-row xl:items-start xl:justify-between">
                <div className="space-y-2">
                  <div className="flex items-start gap-3">
                    <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-slate-900 text-white shadow-sm" data-testid="inventory-header-icon">
                      <Boxes className="h-6 w-6" />
                    </div>
                    <div>
                    <h1 className="text-3xl font-semibold tracking-tight text-slate-900" style={{fontFamily: 'Manrope'}}>
                      Gestão de Inventário
                    </h1>
                    <p className="mt-1 text-sm text-slate-500" data-testid="inventory-header-description">
                      Produtos e stock por loja | Resumo agregado no filtro actual
                    </p>
                    </div>
                  </div>

                  <div className="flex flex-wrap items-center gap-2.5 text-sm" data-testid="inventory-summary-compact-strip">
                    <Badge variant="outline" className="rounded-full bg-white px-3 py-1 text-sm">{inventorySummary.total_products || 0} produtos</Badge>
                    <Badge variant="outline" className="rounded-full bg-white px-3 py-1 text-sm">{inventorySummary.total_quantity || 0} unidades</Badge>
                    <Badge variant="outline" className="rounded-full bg-white px-3 py-1 text-sm">Custo: {formatCurrency(inventorySummary.stock_cost_value || 0)}</Badge>
                    <Badge variant="outline" className="rounded-full bg-white px-3 py-1 text-sm">Venda: {formatCurrency(inventorySummary.stock_sale_value || 0)}</Badge>
                    <Badge variant="outline" className="rounded-full bg-white px-3 py-1 text-sm">Baixo stock: {inventorySummary.low_stock_count || 0}</Badge>
                    <Badge variant="outline" className="rounded-full bg-white px-3 py-1 text-sm">Sem stock: {inventorySummary.zero_stock_count || 0}</Badge>
                    <Badge variant="outline" className="rounded-full bg-white px-3 py-1 text-sm">Rotação 90D: {Number(inventorySummary.rotation_ratio_90d || 0).toFixed(2)}</Badge>
                    <Badge variant="outline" className="rounded-full bg-white px-3 py-1 text-sm">Avisos: {inventorySummary.integrity?.products_with_integrity_warnings || 0}</Badge>
                  </div>
                </div>

                {canAddProduct && (
                  <div className="flex w-full flex-col gap-3 sm:w-auto sm:flex-row sm:items-center">
                    <Button variant="outline" className="h-10 rounded-xl border-slate-300 bg-white px-4 text-sm text-slate-700 shadow-sm transition-transform hover:-translate-y-0.5 hover:border-slate-400 hover:bg-slate-50" onClick={() => setImportDialogOpen(true)} data-testid="import-excel-button">
                      <FileText className="mr-2 h-4 w-4 text-green-600" />
                      Importar Excel
                    </Button>
                    <Button
                      variant="outline"
                      className="h-10 rounded-xl border-slate-300 bg-white px-4 text-sm text-slate-700 shadow-sm transition-transform hover:-translate-y-0.5 hover:border-slate-400 hover:bg-slate-50"
                      onClick={() => {
                        setBulkUpdateUseSelection(selectedProductIds.length > 0);
                        setBulkUpdateSummary(null);
                        setBulkUpdateFile(null);
                        setBulkUpdateDialogOpen(true);
                      }}
                      data-testid="inventory-bulk-update-open-button"
                    >
                      <ArrowRightLeft className="mr-2 h-4 w-4 text-sky-600" />
                      Update Excel
                    </Button>
                    <Dialog open={dialogOpen} onOpenChange={(open) => {
                      setDialogOpen(open);
                      if (!open) {
                        setCreateProductError('');
                        setSkuPreview(null);
                        setSkuManuallyEdited(false);
                      } else {
                        setFormData({
                          ...initialFormData,
                          location_id: locationFilter !== 'all' ? locationFilter : (locations[0]?.id || ''),
                        });
                        setSkuPreview(null);
                        setSkuManuallyEdited(false);
                      }
                    }}>
                      <DialogTrigger asChild>
                        <Button className="h-10 rounded-xl bg-slate-900 px-5 text-sm text-white shadow-[0_12px_30px_-12px_rgba(15,23,42,0.9)] transition-transform hover:-translate-y-0.5 hover:bg-slate-800" data-testid="add-product-button">
                          <Plus className="mr-2 h-4 w-4" />
                          Novo Produto
                        </Button>
                      </DialogTrigger>
            <DialogContent className="w-[min(92vw,1480px)] max-w-[1480px] max-h-[90vh] overflow-y-auto p-4 sm:p-5 xl:p-6">
              <DialogHeader>
                <DialogTitle>Adicionar Novo Produto</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4" data-testid="add-product-form">
                {createProductError && (
                  <Alert variant="destructive" data-testid="product-create-error-alert">
                    <AlertDescription>{createProductError}</AlertDescription>
                  </Alert>
                )}

                {/* Duplicate Warning */}
                {showDuplicateWarning && similarProducts.length > 0 && (
                  <div className="border border-amber-300 bg-amber-50 rounded-lg p-3">
                    <div className="flex items-start gap-2">
                      <AlertTriangle className="h-5 w-5 text-amber-600 mt-0.5" />
                      <div className="flex-1">
                        <p className="font-medium text-amber-800 text-sm">Produtos Semelhantes Encontrados</p>
                        <p className="text-xs text-amber-700 mb-2">
                          Verifique se este produto já existe antes de criar um duplicado:
                        </p>
                        <div className="space-y-1">
                          {similarProducts.map((p) => {
                            const isExactSkuMatch = Boolean(formData.sku) && (p.sku || '').toLowerCase() === formData.sku.toLowerCase();
                            const isExactBarcodeMatch = Boolean(formData.barcode) && (p.barcode || '').toLowerCase() === formData.barcode.toLowerCase();
                            const isExactIdentityMatch = isExactSkuMatch || isExactBarcodeMatch;
                            return (
                              <div 
                                key={p.id} 
                                className="flex items-center justify-between bg-white rounded p-2 text-sm border border-amber-200"
                              >
                                <div>
                                  <span className="font-medium">{p.name}</span>
                                  <span className="text-muted-foreground ml-2">({p.sku})</span>
                                  {p.barcode ? <span className="text-muted-foreground ml-2">• {p.barcode}</span> : null}
                                </div>
                                <div className="flex items-center gap-2">
                                  <Button 
                                    type="button"
                                    size="sm" 
                                    variant="outline"
                                    onClick={() => {
                                      if (isExactIdentityMatch) {
                                        setDialogOpen(false);
                                        setCreateProductError('');
                                        openAddMoreDialog(p);
                                        return;
                                      }
                                      applyExistingProduct(p);
                                    }}
                                    data-testid={`duplicate-product-action-${p.id}`}
                                  >
                                    {isExactSkuMatch ? (p.is_trackable ? 'Registar unidade neste SKU' : 'Adicionar stock neste SKU') : 'Usar como base'}
                                  </Button>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    </div>
                  </div>
                )}
                
                <div className="grid gap-6 xl:grid-cols-[minmax(0,1.2fr)_minmax(320px,0.8fr)]">
                  <div className="space-y-4">
                    <div className="grid gap-4 md:grid-cols-2">
                      <div className="space-y-2">
                        <Label htmlFor="name">Nome do Produto *</Label>
                        <Input
                          id="name"
                          value={formData.name}
                          onChange={(e) => setFormData({...formData, name: e.target.value})}
                          placeholder="Ex: Samsung Galaxy A06"
                          required
                          data-testid="product-name-input"
                        />
                      </div>
                      <div className="space-y-2">
                        <div className="flex items-center justify-between gap-2">
                          <Label htmlFor="sku">SKU / Código</Label>
                          {skuPreviewLoading ? (
                            <Badge variant="outline" data-testid="product-sku-preview-loading-badge">A gerar…</Badge>
                          ) : skuPreview?.code && (
                            <Badge variant="outline" data-testid="product-sku-preview-badge">
                              Auto: {skuPreview.code}
                            </Badge>
                          )}
                        </div>
                        <Input
                          id="sku"
                          value={formData.sku}
                          onChange={(e) => {
                            const nextValue = e.target.value.toUpperCase();
                            setSkuManuallyEdited(Boolean(nextValue.trim()));
                            setFormData({...formData, sku: nextValue});
                          }}
                          placeholder={skuPreview?.code || 'Deixe vazio para gerar automaticamente'}
                          data-testid="product-sku-input"
                          className={products.some((product) => (product.sku || '').toLowerCase() === formData.sku.toLowerCase()) ? 'border-red-500' : ''}
                        />
                        <p className="text-xs text-muted-foreground" data-testid="product-sku-helper-text">
                          Manual tem prioridade. Se deixar vazio, o sistema gera automaticamente com prefixo da subcategoria ou categoria + base36 de 3 posições (001 até ZZZ).
                        </p>
                      </div>
                    </div>

                    <div className="grid gap-4 md:grid-cols-4">
                      <div className="space-y-2">
                        <Label htmlFor="brand">Marca</Label>
                        <Input id="brand" value={formData.brand} onChange={(e) => setFormData({...formData, brand: e.target.value})} placeholder="Samsung, Apple, HP" />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="model">Modelo</Label>
                        <Input id="model" value={formData.model} onChange={(e) => setFormData({...formData, model: e.target.value})} placeholder="Galaxy A06, iPhone 13" />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="part_number">Part Number</Label>
                        <Input id="part_number" value={formData.part_number} onChange={(e) => setFormData({...formData, part_number: e.target.value})} placeholder="SM-A065F/DS" />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="barcode">Código de Barras</Label>
                        <Input
                          id="barcode"
                          value={formData.barcode}
                          onChange={(e) => setFormData({...formData, barcode: e.target.value.toUpperCase()})}
                          placeholder="6901234567890"
                          data-testid="product-barcode-input"
                          className={products.some((product) => (product.barcode || '').toLowerCase() === formData.barcode.toLowerCase()) ? 'border-red-500' : ''}
                        />
                      </div>
                    </div>

                    <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4 rounded-2xl border border-slate-200 p-4" data-testid="product-main-attributes-form-section">
                      {getPrimaryAttributeRules(currentSchema).map((rule) => (
                        <div className="space-y-2" key={rule.key}>
                          <Label htmlFor={`product-${rule.key}-input`}>
                            {rule.label}{rule.required ? ' *' : ''}
                          </Label>
                          <Input
                            id={`product-${rule.key}-input`}
                            value={formData[rule.key] || ''}
                            onChange={(e) => setFormData({ ...formData, [rule.key]: e.target.value })}
                            data-testid={`product-${rule.key}-input`}
                            placeholder={PRIMARY_ATTRIBUTE_PLACEHOLDERS[rule.key] || `Ex: ${rule.label}`}
                          />
                          <div className="flex flex-wrap gap-2 text-xs text-slate-500">
                            {rule.highlight ? <Badge variant="outline">Destaque</Badge> : null}
                            {rule.variant_policy && rule.variant_policy !== 'attribute_only' ? (
                              <Badge variant="secondary">{VARIANT_POLICY_LABELS[rule.variant_policy] || rule.variant_policy}</Badge>
                            ) : null}
                          </div>
                        </div>
                      ))}
                    </div>

                    {currentCategoryGovernance && (
                      <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4" data-testid="product-schema-governance-panel">
                        <div className="flex flex-wrap items-center gap-2">
                          <Badge variant="outline">Schema {currentSchema?.display_name || currentCategoryGovernance.name}</Badge>
                          <Badge variant="secondary">Versão {currentSchema?.version || currentCategoryGovernance.schema_version || 1}</Badge>
                          <Badge variant={currentCategoryGovernance.specs_required ? 'default' : 'outline'}>
                            {currentCategoryGovernance.specs_required ? 'Specs obrigatórias' : 'Specs opcionais'}
                          </Badge>
                        </div>
                        <p className="mt-3 text-sm text-slate-600" data-testid="product-variant-policy-summary">
                          {getVariantPolicySummary(currentSchema)}
                        </p>
                        {formData.variant_parent_product_id ? (
                          <p className="mt-2 text-xs text-slate-500" data-testid="product-variant-parent-note">
                            Está a criar uma nova variante com SKU próprio, sem alterar o SKU nem o stock do produto base.
                          </p>
                        ) : null}
                      </div>
                    )}

                    <div className="grid gap-4 md:grid-cols-2">
                      <div className="space-y-2">
                        <Label htmlFor="category">Categoria *</Label>
                        <Select value={formData.category} onValueChange={handleCategoryChange}>
                          <SelectTrigger data-testid="product-category-select">
                            <SelectValue placeholder="Selecionar categoria" />
                          </SelectTrigger>
                          <SelectContent>
                            {safeProductCategories.map(cat => {
                              const IconComp = CATEGORY_ICONS[cat.base_prefix?.toUpperCase()] || Package;
                              return (
                                <SelectItem key={cat.id} value={cat.base_prefix}>
                                  <div className="flex items-center gap-2">
                                    <IconComp className="h-4 w-4" />
                                    {cat.name}
                                  </div>
                                </SelectItem>
                              );
                            })}
                          </SelectContent>
                        </Select>
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="subcategory">Subcategoria</Label>
                        <Select
                          value={formData.subcategory || ''}
                          onValueChange={(v) => setFormData((prev) => ({
                            ...prev,
                            subcategory: v,
                            invalidSubcategory: false,
                            sku: skuManuallyEdited && prev.sku ? prev.sku : '',
                          }))}
                          disabled={!formData.category || getSubcategoriesForCategory(formData.category).length === 0}
                        >
                          <SelectTrigger data-testid="product-subcategory-select">
                            <SelectValue placeholder={formData.category ? "Selecionar subcategoria" : "Selecione categoria primeiro"} />
                          </SelectTrigger>
                          <SelectContent>
                            {getSubcategoriesForCategory(formData.category).map(sub => (
                              <SelectItem key={sub.id} value={sub.id}>
                                {sub.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        {formData.invalidSubcategory && (
                          <p className="text-xs text-amber-700" data-testid="product-subcategory-invalid-warning">
                            A subcategoria anterior já não é válida para esta categoria. Seleccione uma nova opção.
                          </p>
                        )}
                      </div>
                    </div>

                    {currentSchema && (
                      <div className="rounded-2xl border bg-slate-50 p-4">
                        <h3 className="mb-3 flex items-center gap-2 font-medium">
                          <Settings className="h-4 w-4 text-indigo-600" />
                          Especificações Técnicas - {currentSchema.display_name}
                        </h3>
                        <DynamicSpecsForm
                          schema={currentSchema}
                          specs={formData.specs}
                          onChange={(newSpecs) => setFormData({...formData, specs: newSpecs})}
                          errors={specsErrors}
                        />
                      </div>
                    )}
                  </div>

                  <div className="space-y-4">
                    <ProductPosDescriptionConfigurator
                      category={formData.category}
                      catalogRule={currentCatalogPosRule}
                      value={formData}
                      brand={formData.brand}
                      model={formData.model}
                      specs={formData.specs}
                      onChange={(updates) => setFormData((prev) => ({ ...prev, ...updates }))}
                      testIdPrefix="product"
                    />

                    <div className="flex items-center space-x-2 rounded-2xl border bg-amber-50 p-4">
                      <input
                        type="checkbox"
                        id="is_trackable"
                        checked={formData.is_trackable}
                        onChange={(e) => setFormData({
                          ...formData,
                          is_trackable: e.target.checked,
                          initial_stock: e.target.checked ? '0' : formData.initial_stock
                        })}
                        className="h-4 w-4"
                      />
                      <Label htmlFor="is_trackable" className="cursor-pointer">
                        <span className="flex items-center gap-2 font-medium">
                          <Smartphone className="h-4 w-4" />
                          Produto Rastreável (IMEI/Número de Série)
                        </span>
                        <p className="mt-1 text-xs text-muted-foreground">
                          Marque para telemóveis, tablets, laptops e artigos com rastreio individual.
                        </p>
                      </Label>
                    </div>

                    <div className="grid gap-4 md:grid-cols-2">
                      <div className="space-y-2">
                        <Label htmlFor="cost_price">Preço de Custo (MZN) *</Label>
                        <Input id="cost_price" type="number" step="0.01" value={formData.cost_price} onChange={(e) => setFormData({...formData, cost_price: e.target.value})} required />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="unit_price">Preço de Venda (MZN) *</Label>
                        <Input id="unit_price" type="number" step="0.01" value={formData.unit_price} onChange={(e) => setFormData({...formData, unit_price: e.target.value})} required />
                      </div>
                    </div>

                    <div className="space-y-2 rounded-2xl border border-slate-200 p-4">
                      <Label htmlFor="warranty_option_id" className="flex items-center gap-2">
                        <Shield className="h-4 w-4 text-green-600" />
                        Garantia do Produto
                      </Label>
                      <Select
                        value={formData.warranty_option_id || 'none'}
                        onValueChange={(value) => {
                          if (value === 'none') {
                            setFormData({ ...formData, warranty_option_id: '', warranty_days: '' });
                          } else {
                            const selectedOption = warrantyOptions.find(w => w.id === value);
                            setFormData({ ...formData, warranty_option_id: value, warranty_days: selectedOption?.days || '' });
                          }
                        }}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Seleccionar período de garantia" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">Sem Garantia</SelectItem>
                          {warrantyOptions.map((opt) => (
                            <SelectItem key={opt.id} value={opt.id}>
                              {opt.name} {opt.days ? `(${opt.days} dias)` : ''}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <p className="text-xs text-muted-foreground">
                        Quando este produto for vendido, a garantia será calculada automaticamente.
                      </p>
                    </div>

                    <div className="grid gap-4 md:grid-cols-2">
                      <div className="space-y-2">
                        <Label htmlFor="reorder_level">Nível de Reposição *</Label>
                        <Input id="reorder_level" type="number" value={formData.reorder_level} onChange={(e) => setFormData({...formData, reorder_level: e.target.value})} required />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="new-product-location">Localização do stock inicial</Label>
                        <Select value={formData.location_id} onValueChange={(value) => setFormData({ ...formData, location_id: value })}>
                          <SelectTrigger id="new-product-location" data-testid="inventory-new-product-location-select">
                            <SelectValue placeholder="Seleccione a localização" />
                          </SelectTrigger>
                          <SelectContent>
                            {locations.map((loc) => (
                              <SelectItem key={loc.id} value={loc.id}>{loc.name}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="initial_stock">Stock Inicial {formData.is_trackable && '(N/A)'}</Label>
                        <Input id="initial_stock" type="number" value={formData.initial_stock} onChange={(e) => setFormData({...formData, initial_stock: e.target.value})} disabled={formData.is_trackable} />
                      </div>
                    </div>
                  </div>
                </div>
                
                <div className="flex justify-end gap-2 pt-4 border-t">
                  <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                    Cancelar
                  </Button>
                  <Button type="submit" data-testid="submit-product-button">
                    {formData.is_trackable ? 'Criar e Registar Items' : 'Criar Produto'}
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
                  </div>
                )}
              </div>

              <div className="rounded-[24px] border border-slate-200 bg-white/90 p-4 shadow-sm" data-testid="inventory-control-toolbar">
                <div className="flex flex-col gap-4">
                  <div className="flex flex-wrap items-center gap-3">
                    <div className="relative min-w-[240px] flex-[1.25]">
                      <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                      <Input
                        placeholder="Pesquisar por nome, SKU, marca ou modelo..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="h-10 rounded-xl border-slate-200 bg-slate-50 pl-10 pr-10 text-sm text-slate-900 shadow-inner focus-visible:ring-slate-400"
                        data-testid="inventory-search-input"
                      />
                      {searchQuery && (
                        <Button
                          variant="ghost"
                          size="sm"
                          className="absolute right-1 top-1/2 h-6 w-6 -translate-y-1/2 rounded-full p-0 text-slate-500 hover:bg-slate-200"
                          onClick={() => setSearchQuery('')}
                          data-testid="inventory-search-clear-button"
                        >
                          <X className="h-3.5 w-3.5" />
                        </Button>
                      )}
                    </div>

                    <Select value={locationFilter} onValueChange={setLocationFilter}>
                      <SelectTrigger className="h-10 w-[170px] rounded-xl border-slate-200 bg-slate-50 text-sm shadow-sm" data-testid="location-filter">
                        <MapPin className="mr-2 h-4 w-4 text-slate-400" />
                        <SelectValue placeholder="Localização" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">Todas Localizações</SelectItem>
                        {locations.map((loc) => (
                          <SelectItem key={loc.id} value={loc.id}>
                            {loc.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>

                      <Select value={stateFilter} onValueChange={setStateFilter}>
                      <SelectTrigger className="h-10 w-[160px] rounded-xl border-slate-200 bg-slate-50 text-sm shadow-sm" data-testid="state-filter">
                        <Filter className="mr-2 h-4 w-4 text-slate-400" />
                        <SelectValue placeholder="Estado" />
                      </SelectTrigger>
                      <SelectContent>
                          {INVENTORY_STATE_OPTIONS.map((option) => (
                            <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>
                          ))}
                      </SelectContent>
                    </Select>

                    <Select value={trackableFilter} onValueChange={setTrackableFilter}>
                      <SelectTrigger className="h-10 w-[170px] rounded-xl border-slate-200 bg-slate-50 text-sm shadow-sm" data-testid="trackable-filter">
                        <Barcode className="mr-2 h-4 w-4 text-slate-400" />
                        <SelectValue placeholder="Tipo" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">Todos Tipos</SelectItem>
                        <SelectItem value="true">Rastreáveis</SelectItem>
                        <SelectItem value="false">Não Rastreáveis</SelectItem>
                      </SelectContent>
                    </Select>

                    {activeTab !== 'all' && currentSubcategories.length > 0 && (
                      <Select value={subcategoryFilter} onValueChange={setSubcategoryFilter}>
                        <SelectTrigger className="h-10 w-[196px] rounded-xl border-slate-200 bg-slate-50 text-sm shadow-sm" data-testid="subcategory-filter">
                          <Layers className="mr-2 h-4 w-4 text-slate-400" />
                          <SelectValue placeholder="Subcategoria" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">Todas Subcategorias</SelectItem>
                          {currentSubcategories.map((sub) => (
                            <SelectItem key={sub.id} value={sub.id}>
                              <span className="mr-2 font-mono text-xs text-slate-500">{sub.prefix}</span>
                              {sub.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    )}

                    {isAdmin && (
                      <Button
                        variant={showDeleted ? 'default' : 'outline'}
                        size="sm"
                        className={cn(
                          'h-10 rounded-xl px-4 text-sm shadow-sm',
                          showDeleted
                            ? 'bg-slate-900 text-white hover:bg-slate-800'
                            : 'border-slate-200 bg-white text-slate-700 hover:bg-slate-50'
                        )}
                        onClick={() => setShowDeleted(!showDeleted)}
                        data-testid="show-deleted-toggle"
                      >
                        <Trash2 className="mr-2 h-4 w-4" />
                        {showDeleted ? 'A ver eliminados' : 'Eliminados'}
                      </Button>
                    )}

                    {activeFiltersCount > 0 && (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-10 rounded-xl border border-slate-200 bg-slate-50 px-4 text-sm text-slate-700 hover:bg-slate-100"
                        onClick={handleClearFilters}
                        data-testid="clear-filters-button"
                      >
                        <X className="mr-2 h-4 w-4" />
                        Limpar filtros
                      </Button>
                    )}

                    <div className="flex items-center gap-1" data-testid="inventory-export-panel">
                      <ExportButton
                        module="inventory"
                        label="Exp."
                        filters={{
                          location_id: locationFilter !== 'all' ? locationFilter : null,
                          state: stateFilter !== 'all' ? stateFilter : null,
                          trackable: trackableFilter !== 'all' ? trackableFilter === 'true' : null,
                          category_prefix: activeTab !== 'all' ? activeTab : null,
                          subcategory_id: subcategoryFilter !== 'all' ? subcategoryFilter : null,
                          search: searchQuery.trim() || null,
                        }}
                      />
                    </div>
                  </div>

                  <div className="flex flex-col gap-3 xl:flex-row xl:items-center xl:justify-between">
                    {inventoryStateChips.length > 0 && (
                      <div className="flex flex-wrap items-center gap-2" data-testid="inventory-advanced-state-chips">
                        {inventoryStateChips.map((option) => (
                          <Button
                            key={option.value}
                            type="button"
                            variant={stateFilter === option.value ? 'default' : 'outline'}
                            size="sm"
                            className={cn(
                              'h-9 rounded-full px-3.5 text-sm shadow-sm',
                              stateFilter === option.value
                                ? 'bg-slate-900 text-white hover:bg-slate-800'
                                : 'border-slate-200 bg-white text-slate-600 hover:bg-slate-50'
                            )}
                            onClick={() => setStateFilter((current) => current === option.value ? 'all' : option.value)}
                            data-testid={`inventory-state-chip-${option.value}`}
                          >
                            {option.shortLabel}
                            <span className="ml-2 rounded-full bg-black/5 px-2 py-0.5 text-xs font-semibold text-current">
                              {option.count}
                            </span>
                          </Button>
                        ))}
                      </div>
                    )}

                    <ScrollArea className="w-full" data-testid="inventory-category-focus-bar">
                      <TabsList className="inline-flex h-auto min-w-max items-center justify-start gap-1 rounded-2xl border border-slate-200 bg-slate-50 p-1">
                        <TabsTrigger
                          value="all"
                          className="h-9 rounded-xl px-3 text-sm font-medium text-slate-600 data-[state=active]:bg-slate-900 data-[state=active]:text-white"
                          data-testid="tab-all"
                        >
                          <LayoutGrid className="mr-1 h-3.5 w-3.5" />
                          Todos
                          <span className="ml-2 rounded-full bg-white/80 px-2 py-0.5 text-xs font-semibold text-slate-600 data-[state=active]:text-white">
                            {totalInventoryQuantifier}
                          </span>
                        </TabsTrigger>

                        {orderedCategories.map((cat) => {
                          const IconComp = CATEGORY_ICONS[cat.base_prefix] || Package;
                          const quantifier = categoryQuantifiers[cat.base_prefix] || { quantity: 0, products: 0 };
                          const labels = CATEGORY_LABELS[cat.base_prefix] || { full: cat.name, short: cat.name };

                          return (
                            <Tooltip key={cat.base_prefix}>
                              <TooltipTrigger asChild>
                                <TabsTrigger
                                  value={cat.base_prefix}
                                  className="h-9 rounded-xl px-3 text-sm font-medium text-slate-600 data-[state=active]:bg-indigo-600 data-[state=active]:text-white"
                                  data-testid={`tab-${cat.base_prefix.toLowerCase()}`}
                                >
                                  <IconComp className="mr-1 h-3.5 w-3.5" />
                                  <span className="hidden 2xl:inline">{labels.full}</span>
                                  <span className="2xl:hidden">{labels.short}</span>
                                  <span className="ml-2 rounded-full bg-white/80 px-2 py-0.5 text-xs font-semibold text-slate-600 data-[state=active]:text-white">
                                    {quantifier.quantity}
                                  </span>
                                </TabsTrigger>
                              </TooltipTrigger>
                              <TooltipContent side="bottom" className="text-sm">
                                <p>{labels.full}</p>
                                <p className="text-muted-foreground">{quantifier.products} produto{quantifier.products !== 1 ? 's' : ''} • {quantifier.quantity} unidade{quantifier.quantity !== 1 ? 's' : ''}</p>
                              </TooltipContent>
                            </Tooltip>
                          );
                        })}
                      </TabsList>
                      <ScrollBar orientation="horizontal" />
                    </ScrollArea>
                  </div>
                </div>
              </div>
            </div>
          </div>
      
      {/* Import Wizard */}
      <ImportWizard 
        entity="products"
        categories={productCategories}
        defaultCategoryId={activeTab !== 'all' ? (productCategories.find((category) => getCanonicalCategoryTab(category.base_prefix) === activeTab)?.id || 'all') : 'all'}
        open={importDialogOpen}
        onOpenChange={setImportDialogOpen}
        onSuccess={loadProducts}
      />
          {/* Tab Content */}
          <TabsContent value={activeTab} className="mt-0 pt-1">
            <Card className="overflow-hidden rounded-[24px] border-slate-200 bg-white shadow-sm" data-testid="products-table">
              <CardHeader className="border-b border-slate-200 bg-white/90 px-5 py-4 md:px-6">
                <div className="flex items-start justify-between gap-3 overflow-hidden">
                  <CardTitle className="flex min-w-0 items-center gap-2.5 text-xl font-semibold text-slate-900">
                    <Package className="h-5 w-5" />
                    <span className="truncate" data-testid="inventory-table-title">
                      {activeTab === 'all' ? `Todos os Produtos (${filteredProducts.length})` : `${activeCategoryLabel} (${filteredProducts.length})`}
                    </span>
                  </CardTitle>

                  <div className="ml-auto flex shrink-0 items-center gap-2 text-sm">
                    <Select value={inventorySortField} onValueChange={setInventorySortField}>
                      <SelectTrigger className="h-9 w-[160px] rounded-xl border-slate-200 bg-white px-3 text-sm" data-testid="inventory-sort-field-select">
                        <SelectValue placeholder="Ordenar por" />
                      </SelectTrigger>
                      <SelectContent>
                        {INVENTORY_SORT_OPTIONS.map((option) => (
                          <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Button
                      type="button"
                      variant={inventorySortDirection === 'asc' ? 'default' : 'outline'}
                      size="sm"
                      className="h-9 rounded-xl px-3 text-sm"
                      onClick={() => setInventorySortDirection('asc')}
                      data-testid="inventory-sort-asc-button"
                    >
                      A-Z
                    </Button>
                    <Button
                      type="button"
                      variant={inventorySortDirection === 'desc' ? 'default' : 'outline'}
                      size="sm"
                      className="h-9 rounded-xl px-3 text-sm"
                      onClick={() => setInventorySortDirection('desc')}
                      data-testid="inventory-sort-desc-button"
                    >
                      Z-A
                    </Button>
                    {selectedProductIds.length > 0 && (
                      <div className="ml-1 flex shrink-0 items-center gap-2 whitespace-nowrap" data-testid="inventory-table-bulk-actions-row">
                        <Badge variant="outline" className="rounded-full border-slate-200 bg-slate-50 px-2.5 py-1 text-xs text-slate-700" data-testid="inventory-table-selection-badge">
                          {selectedProductIds.length} sel.
                        </Badge>
                        <Button
                          variant="outline"
                          size="sm"
                          className="h-9 rounded-xl px-3 text-sm"
                          onClick={() => handleBulkProductStatus('deactivate')}
                          data-testid="inventory-table-bulk-deactivate-button"
                        >
                          Desat.
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          className="h-9 rounded-xl px-3 text-sm"
                          onClick={() => handleBulkProductStatus('restore_soft')}
                          data-testid="inventory-table-bulk-restore-button"
                        >
                          Rest.
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          className="h-9 rounded-xl px-3 text-sm"
                          onClick={openBulkDeleteDialog}
                          data-testid="inventory-table-bulk-delete-button"
                        >
                          Apagar
                        </Button>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button
                              variant="outline"
                              size="sm"
                              className="h-9 rounded-xl px-3 text-sm"
                              data-testid="inventory-table-bulk-more-button"
                            >
                              Mais
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end" className="w-40 rounded-xl border border-slate-200 bg-white p-1 shadow-xl" data-testid="inventory-table-bulk-more-menu">
                            <DropdownMenuItem onClick={handleExportSelectedProducts} data-testid="inventory-table-bulk-export-button">
                              Exportar CSV
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem
                              onClick={() => {
                                setBulkUpdateUseSelection(true);
                                setBulkUpdateSummary(null);
                                setBulkUpdateFile(null);
                                setBulkUpdateDialogOpen(true);
                              }}
                              data-testid="inventory-table-bulk-update-template-button"
                            >
                              Update Excel
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                    )}
                  </div>
                </div>
              </CardHeader>
            <CardContent className="p-0">
              {loading ? (
                <div className="flex items-center justify-center py-12">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
                </div>
              ) : filteredProducts.length === 0 ? (
                <div className="text-center py-12">
                  <Package className="h-12 w-12 mx-auto text-muted-foreground/50 mb-4" />
                  <p className="text-muted-foreground">
                    {searchQuery || subcategoryFilter !== 'all' 
                      ? 'Nenhum produto encontrado com os filtros aplicados' 
                      : 'Sem produtos nesta categoria'}
                  </p>
                  {(searchQuery || subcategoryFilter !== 'all') && (
                    <Button variant="link" onClick={handleClearFilters} className="mt-2">
                      Limpar filtros
                    </Button>
                  )}
                </div>
              ) : (
                <div className="w-full overflow-x-auto" ref={inventoryTableWrapperRef} data-testid="inventory-table-scroll-shell">
                  <Table className={`min-w-[1080px] ${TABLE_DENSITY.table} text-sm`}>
                    <colgroup>
                      {inventoryColumnWidths.map((width, index) => <col key={`inventory-column-${index}`} style={{ width: `${width}%` }} />)}
                    </colgroup>
                    <TableHeader className="sticky top-0 z-20 bg-white shadow-sm" data-testid="inventory-sticky-column-header">
                      <TableRow className="border-b border-slate-200 bg-slate-50/95 hover:bg-slate-50/95">
                        <TableHead className={`relative ${INVENTORY_HEAD_CLASS}`}>
                          <Checkbox
                            checked={allProductsSelected}
                            onCheckedChange={(checked) => toggleSelectAllProducts(Boolean(checked))}
                            data-testid="inventory-select-all-checkbox"
                          />
                          <button type="button" className="absolute inset-y-0 right-0 w-3 cursor-col-resize hover:bg-slate-100/70" onMouseDown={(event) => startInventoryColumnResize(0, event)} data-testid="inventory-column-splitter-0"><span className="mx-auto block h-full w-px bg-slate-300" /></button>
                        </TableHead>
                        <TableHead className={`relative ${INVENTORY_HEAD_CLASS}`}><button type="button" className="inline-flex items-center gap-1" onClick={() => handleInventorySort('product')} data-testid="inventory-sort-product-header">Produto {renderInventorySortIndicator('product')}</button><button type="button" className="absolute inset-y-0 right-0 w-3 cursor-col-resize hover:bg-slate-100/70" onMouseDown={(event) => startInventoryColumnResize(1, event)} data-testid="inventory-column-splitter-1"><span className="mx-auto block h-full w-px bg-slate-300" /></button></TableHead>
                        <TableHead className={`relative ${INVENTORY_HEAD_CLASS}`}><button type="button" className="inline-flex items-center gap-1" onClick={() => handleInventorySort('code')} data-testid="inventory-sort-code-header">Código {renderInventorySortIndicator('code')}</button><button type="button" className="absolute inset-y-0 right-0 w-3 cursor-col-resize hover:bg-slate-100/70" onMouseDown={(event) => startInventoryColumnResize(2, event)} data-testid="inventory-column-splitter-2"><span className="mx-auto block h-full w-px bg-slate-300" /></button></TableHead>
                        <TableHead className={`relative ${INVENTORY_HEAD_CLASS}`}><button type="button" className="inline-flex items-center gap-1" onClick={() => handleInventorySort('classification')} data-testid="inventory-sort-classification-header">Classificação {renderInventorySortIndicator('classification')}</button><button type="button" className="absolute inset-y-0 right-0 w-3 cursor-col-resize hover:bg-slate-100/70" onMouseDown={(event) => startInventoryColumnResize(3, event)} data-testid="inventory-column-splitter-3"><span className="mx-auto block h-full w-px bg-slate-300" /></button></TableHead>
                        <TableHead className={`relative ${INVENTORY_HEAD_CLASS}`}><button type="button" className="inline-flex items-center gap-1" onClick={() => handleInventorySort('summary')} data-testid="inventory-sort-summary-header">Resumo técnico {renderInventorySortIndicator('summary')}</button><button type="button" className="absolute inset-y-0 right-0 w-3 cursor-col-resize hover:bg-slate-100/70" onMouseDown={(event) => startInventoryColumnResize(4, event)} data-testid="inventory-column-splitter-4"><span className="mx-auto block h-full w-px bg-slate-300" /></button></TableHead>
                        <TableHead className={`relative ${INVENTORY_HEAD_CLASS} text-right`}><button type="button" className="inline-flex items-center gap-1 justify-end" onClick={() => handleInventorySort('stock')} data-testid="inventory-sort-stock-header">Stock {renderInventorySortIndicator('stock')}</button><button type="button" className="absolute inset-y-0 right-0 w-3 cursor-col-resize hover:bg-slate-100/70" onMouseDown={(event) => startInventoryColumnResize(5, event)} data-testid="inventory-column-splitter-5"><span className="mx-auto block h-full w-px bg-slate-300" /></button></TableHead>
                        <TableHead className={`relative ${INVENTORY_HEAD_CLASS} text-right`}><button type="button" className="inline-flex items-center gap-1 justify-end" onClick={() => handleInventorySort('price')} data-testid="inventory-sort-price-header">Preço {renderInventorySortIndicator('price')}</button><button type="button" className="absolute inset-y-0 right-0 w-3 cursor-col-resize hover:bg-slate-100/70" onMouseDown={(event) => startInventoryColumnResize(6, event)} data-testid="inventory-column-splitter-6"><span className="mx-auto block h-full w-px bg-slate-300" /></button></TableHead>
                        <TableHead className={`relative ${INVENTORY_HEAD_CLASS} text-center`}>Estado<button type="button" className="absolute inset-y-0 right-0 w-3 cursor-col-resize hover:bg-slate-100/70" onMouseDown={(event) => startInventoryColumnResize(7, event)} data-testid="inventory-column-splitter-7"><span className="mx-auto block h-full w-px bg-slate-300" /></button></TableHead>
                        <TableHead className={`${INVENTORY_HEAD_CLASS} text-center`}>Acções</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {paginatedInventoryProducts.pageItems.map((product) => {
                        const categoryPrefix = getCategoryPrefix(product);
                        const IconComp = CATEGORY_ICONS[categoryPrefix] || Package;
                        const subcategoryLabel = getProductSubcategoryLabel(product);
                        const displayedStock = getDisplayedStockForProduct(product);
                        const stockStatus = getStockStatusMeta(displayedStock, product.reorder_level);
                        
                        return (
                          <TableRow 
                            key={product.id} 
                            className={cn(
                              "group border-b border-slate-100 transition-all hover:bg-indigo-50/35",
                              product.is_deleted && "bg-red-50/40 opacity-70"
                            )}
                            data-testid={`inventory-product-row-${product.id}`}
                          >
                            <TableCell className="px-3 py-3 align-top">
                              <Checkbox
                                checked={selectedProductIds.includes(product.id)}
                                onCheckedChange={(checked) => toggleProductSelection(product.id, Boolean(checked))}
                                data-testid={`inventory-product-select-checkbox-${product.id}`}
                              />
                            </TableCell>
                            <TableCell className="px-3 py-3 align-top">
                              <div className="flex items-start gap-3">
                                <div className={cn(
                                  "rounded-xl border p-2 shadow-sm",
                                  product.is_trackable ? "border-indigo-200 bg-indigo-50" : "border-slate-200 bg-slate-100"
                                )}>
                                  <IconComp className={cn(
                                    "h-4 w-4",
                                    product.is_trackable ? "text-indigo-600" : "text-slate-600"
                                  )} />
                                </div>
                                <div className="min-w-0">
                                  <p className="truncate text-sm font-semibold leading-5 text-slate-900" data-testid={`product-name-${product.id}`}>
                                    {product.name}
                                  </p>
                                  <p className="truncate text-xs text-slate-500">
                                    {[product.brand, product.model].filter(Boolean).join(' • ') || 'Sem marca/modelo'}
                                  </p>
                                  {product.is_deleted && (
                                    <Badge variant="destructive" className="mt-1 text-xs">Eliminado</Badge>
                                  )}
                                </div>
                              </div>
                            </TableCell>
                            <TableCell className="px-3 py-3 align-top">
                              <div>
                                <code className="inline-flex rounded-lg border border-slate-200 bg-slate-100 px-2 py-1 text-xs font-semibold text-slate-700" data-testid={`product-sku-${product.id}`}>
                                  {product.sku}
                                </code>
                              </div>
                            </TableCell>
                            <TableCell className="px-3 py-3 align-top">
                              <div className="flex flex-wrap gap-1.5">
                                <Badge variant="outline" className="w-fit rounded-full border-slate-300 bg-white px-2.5 py-0.5 text-xs text-slate-700" data-testid={`product-category-badge-${product.id}`}>
                                  {getProductCategoryLabel(product)}
                                </Badge>
                                {subcategoryLabel && (
                                  <Badge variant="secondary" className="w-fit rounded-full bg-slate-100 px-2.5 py-0.5 text-xs text-slate-700" data-testid={`product-subcategory-badge-${product.id}`}>
                                    {subcategoryLabel}
                                  </Badge>
                                )}
                                {product.is_trackable && (
                                  <Badge variant="secondary" className="w-fit rounded-full bg-indigo-50 px-2.5 py-0.5 text-xs text-indigo-700" data-testid={`product-trackable-badge-${product.id}`}>
                                    Track.
                                  </Badge>
                                )}
                                {product.is_variant && (
                                  <Badge variant="outline" className="w-fit rounded-full border-sky-200 bg-sky-50 px-2.5 py-0.5 text-xs text-sky-700" data-testid={`product-variant-badge-${product.id}`}>
                                    Variante
                                  </Badge>
                                )}
                              </div>
                            </TableCell>
                            <TableCell className="px-3 py-3 align-top">
                              <div className="max-w-[260px] space-y-1">
                                <p
                                  className="text-sm leading-5 text-slate-600"
                                  style={{
                                    display: '-webkit-box',
                                    WebkitLineClamp: 1,
                                    WebkitBoxOrient: 'vertical',
                                    overflow: 'hidden',
                                  }}
                                  data-testid={`product-spec-preview-${product.id}`}
                                >
                                  {getCompactSpecText(product)}
                                </p>
                                {product.warranty_days ? <div className="text-xs text-slate-500">Garantia {product.warranty_days}d</div> : null}
                              </div>
                            </TableCell>
                            <TableCell className="px-3 py-3 text-right align-top">
                              <span className={cn(
                                "text-base font-semibold",
                                stockStatus.valueClass
                              )} data-testid={`product-stock-${product.id}`}>
                                {displayedStock}
                              </span>
                              <p className="text-xs text-slate-500">Reposição: {product.reorder_level || 0}</p>
                              <p className="text-xs text-slate-400" data-testid={`product-stock-scope-${product.id}`}>{getProductStockScopeLabel(product)}</p>
                            </TableCell>
                            <TableCell className="px-3 py-3 text-right align-top">
                              <span className="block text-sm font-semibold text-green-600" data-testid={`product-sale-price-${product.id}`}>
                                {formatCurrency(product.unit_price)}
                              </span>
                              {canViewCost && (
                                <span className="block text-xs text-slate-500" data-testid={`product-cost-price-${product.id}`}>
                                  Custo: {formatCurrency(product.cost_price || 0)}
                                </span>
                              )}
                            </TableCell>
                            <TableCell className="px-3 py-3 text-center align-top">
                              <Badge variant="outline" className={cn('rounded-full px-2.5 py-0.5 text-xs', stockStatus.badgeClass)} data-testid={`product-stock-status-${product.id}`}>
                                {displayedStock <= 0 ? <AlertTriangle className="mr-1 h-3 w-3" /> : displayedStock <= product.reorder_level ? <AlertCircle className="mr-1 h-3 w-3" /> : <Check className="mr-1 h-3 w-3" />}
                                {stockStatus.label}
                              </Badge>
                            </TableCell>
                            <TableCell className="px-3 py-3 align-top whitespace-nowrap">
                              <UniversalCrudActions
                                entityLabel="product"
                                entityId={product.id}
                                isDeleted={product.is_deleted}
                                canView
                                canEdit={canEdit && !product.is_deleted}
                                canDelete={canEdit && !product.is_deleted}
                                canRecover={isAdmin && product.is_deleted}
                                align="center"
                                compact
                                onView={() => handleViewProduct(product)}
                                onEdit={() => handleViewProduct(product, 'edit')}
                                onDelete={() => handleOpenDelete(product)}
                                onRecover={() => handleRestoreProduct(product)}
                                extraActions={
                                  <DropdownMenu>
                                    <DropdownMenuTrigger asChild>
                                      <Button
                                        size="sm"
                                        variant="ghost"
                                        className="h-8 w-8 rounded-lg border border-slate-200 bg-slate-50 p-0 text-slate-700 shadow-sm transition-all hover:-translate-y-0.5 hover:border-slate-300 hover:bg-white"
                                        title="Mais ações"
                                        data-testid={`product-actions-menu-trigger-${product.id}`}
                                      >
                                        <MoreHorizontal className="h-4 w-4" />
                                      </Button>
                                    </DropdownMenuTrigger>
                                    <DropdownMenuContent align="end" className="w-60 rounded-2xl border border-slate-200 bg-white p-1 shadow-xl" data-testid={`product-actions-menu-${product.id}`}>
                                      {!product.is_deleted && (
                                        <DropdownMenuItem onClick={() => handleTransferProduct(product)} data-testid={`transfer-product-${product.id}`}>
                                          <ArrowRightLeft className="h-4 w-4 text-blue-600" />
                                          Transferir stock
                                        </DropdownMenuItem>
                                      )}

                                      {canAddProduct && !product.is_deleted && (
                                        <DropdownMenuItem onClick={() => openAddMoreDialog(product)} data-testid={`product-add-more-${product.id}`}>
                                          <PlusCircle className="h-4 w-4 text-green-600" />
                                          {product.is_trackable ? 'Registar unidade' : 'Adicionar stock'}
                                        </DropdownMenuItem>
                                      )}

                                      {product.is_trackable && !product.is_deleted && (
                                        <DropdownMenuItem onClick={() => openTrackableStockManager(product)} data-testid={`manage-trackable-stock-${product.id}`}>
                                          <Hash className="h-4 w-4 text-indigo-600" />
                                          Ver / editar stock rastreável
                                        </DropdownMenuItem>
                                      )}

                                      <DropdownMenuItem onClick={() => handleViewAudit(product)} data-testid={`product-audit-${product.id}`}>
                                        <History className="h-4 w-4 text-indigo-600" />
                                        Ver histórico
                                      </DropdownMenuItem>
                                    </DropdownMenuContent>
                                  </DropdownMenu>
                                }
                              />
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                  <TablePaginationBar
                    page={paginatedInventoryProducts.safePage}
                    totalItems={sortedProducts.length}
                    pageSize={DEFAULT_TABLE_PAGE_SIZE}
                    onPageChange={setInventoryPage}
                    testIdPrefix="inventory-table"
                    itemLabel="produtos"
                  />
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
        </ModuleStateBoundary>
      </TooltipProvider>

      {/* Add Item Dialog */}
      <Dialog open={itemDialogOpen} onOpenChange={setItemDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <PlusCircle className="h-5 w-5 text-indigo-600" />
              Registar Item - {selectedProduct?.name}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleAddItem} className="space-y-4">
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm">
              <p className="font-medium text-amber-800">Registo de Unidade Física</p>
              <p className="text-amber-700 text-xs mt-1">
                Cada item representa uma unidade física com IMEI ou Número de Série único.
              </p>
            </div>
            
            {/* IMEIs */}
            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label htmlFor="imei1">IMEI 1 *</Label>
                <Input
                  id="imei1"
                  value={itemFormData.imei1}
                  onChange={(e) => setItemFormData({...itemFormData, imei1: e.target.value})}
                  placeholder="359XXXXXXXXXXXXXXX"
                  maxLength={15}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="imei2">IMEI 2</Label>
                <Input
                  id="imei2"
                  value={itemFormData.imei2}
                  onChange={(e) => setItemFormData({...itemFormData, imei2: e.target.value})}
                  placeholder="Dual SIM"
                  maxLength={15}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="imei3">IMEI 3 / eSIM</Label>
                <Input
                  id="imei3"
                  value={itemFormData.imei3}
                  onChange={(e) => setItemFormData({...itemFormData, imei3: e.target.value})}
                  maxLength={15}
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="serial_number">Número de Série</Label>
              <Input
                id="serial_number"
                value={itemFormData.serial_number}
                onChange={(e) => setItemFormData({...itemFormData, serial_number: e.target.value})}
                placeholder="S/N do equipamento"
              />
              <p className="text-xs text-muted-foreground">Obrigatório se IMEI1 não for preenchido</p>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="condition">Condição</Label>
                <Select 
                  value={itemFormData.condition} 
                  onValueChange={(value) => setItemFormData({...itemFormData, condition: value})}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {CONDITION_OPTIONS.map(opt => (
                      <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="location_id">Localização *</Label>
                <Select 
                  value={itemFormData.location_id} 
                  onValueChange={(value) => setItemFormData({...itemFormData, location_id: value})}
                >
                  <SelectTrigger data-testid="item-location-select">
                    <SelectValue placeholder="Selecionar localização" />
                  </SelectTrigger>
                  <SelectContent>
                    {locations.filter(loc => loc.type !== 'transit' && loc.type !== 'repair').map(loc => {
                      const IconComp = LOCATION_TYPE_ICONS[loc.type] || MapPin;
                      return (
                        <SelectItem key={loc.id} value={loc.id}>
                          <div className="flex items-center gap-2">
                            <IconComp className="h-4 w-4" />
                            {loc.name}
                          </div>
                        </SelectItem>
                      );
                    })}
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="purchase_price">Preço de Compra (MZN)</Label>
                <Input
                  id="purchase_price"
                  type="number"
                  step="0.01"
                  value={itemFormData.purchase_price}
                  onChange={(e) => setItemFormData({...itemFormData, purchase_price: e.target.value})}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="supplier">Fornecedor</Label>
                <Select 
                  value={itemFormData.supplier} 
                  onValueChange={(value) => setItemFormData({...itemFormData, supplier: value})}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Selecionar fornecedor" />
                  </SelectTrigger>
                  <SelectContent>
                    {suppliers.map(sup => (
                      <SelectItem key={sup.id} value={sup.name}>{sup.name}</SelectItem>
                    ))}
                    <SelectItem value="Outro">Outro</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="notes">Notas</Label>
              <Input
                id="notes"
                value={itemFormData.notes}
                onChange={(e) => setItemFormData({...itemFormData, notes: e.target.value})}
                placeholder="Observações sobre este item"
              />
            </div>
            
            <div className="flex justify-between pt-4 border-t">
              <Button type="button" variant="outline" onClick={closeItemDialog}>
                Fechar
              </Button>
              <Button type="submit">
                Registar Item
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={stockAdjustmentDialogOpen} onOpenChange={(open) => {
        setStockAdjustmentDialogOpen(open);
        if (!open) closeStockAdjustmentDialog();
      }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <PlusCircle className="h-5 w-5 text-indigo-600" />
              Adicionar stock - {stockAdjustmentProduct?.name}
            </DialogTitle>
          </DialogHeader>

          <form onSubmit={handleAdjustStock} className="space-y-4" data-testid="stock-adjustment-form">
            <div className="rounded-lg border bg-slate-50 p-3 text-sm" data-testid="stock-adjustment-product-summary">
              <p className="font-medium">SKU {stockAdjustmentProduct?.sku}</p>
              <p className="text-muted-foreground">Use este fluxo para somar unidades ao mesmo artigo, sempre associando a localização correcta.</p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="stock-adjustment-location">Localização</Label>
              <Select value={stockAdjustmentForm.location_id} onValueChange={(value) => setStockAdjustmentForm((prev) => ({ ...prev, location_id: value }))}>
                <SelectTrigger id="stock-adjustment-location" data-testid="stock-adjustment-location-select">
                  <SelectValue placeholder="Seleccione a localização" />
                </SelectTrigger>
                <SelectContent>
                  {locations.filter((loc) => loc.type !== 'transit' && loc.type !== 'repair').map((loc) => (
                    <SelectItem key={loc.id} value={loc.id}>{loc.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="stock-adjustment-quantity">Quantidade a adicionar</Label>
              <Input
                id="stock-adjustment-quantity"
                type="number"
                min="1"
                value={stockAdjustmentForm.quantity}
                onChange={(e) => setStockAdjustmentForm((prev) => ({ ...prev, quantity: e.target.value }))}
                data-testid="stock-adjustment-quantity-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="stock-adjustment-reason">Motivo</Label>
              <Textarea
                id="stock-adjustment-reason"
                rows={3}
                value={stockAdjustmentForm.reason}
                onChange={(e) => setStockAdjustmentForm((prev) => ({ ...prev, reason: e.target.value }))}
                data-testid="stock-adjustment-reason-textarea"
              />
            </div>

            <div className="flex justify-end gap-2 border-t pt-4">
              <Button type="button" variant="outline" onClick={closeStockAdjustmentDialog} data-testid="stock-adjustment-cancel-button">
                Cancelar
              </Button>
              <Button type="submit" data-testid="stock-adjustment-submit-button">
                Adicionar ao stock
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* View Product Dialog */}
      <Dialog open={viewDialogOpen} onOpenChange={setViewDialogOpen}>
        <DialogContent className="w-[min(92vw,1480px)] max-w-[1480px] max-h-[90vh] overflow-y-auto p-4 sm:p-5 xl:p-6">
          <DialogHeader>
            <DialogTitle>Detalhes do Produto</DialogTitle>
          </DialogHeader>
          {selectedProduct && (
            <div className="space-y-4">
              <div className="grid gap-4 md:grid-cols-2">
                <div>
                  <p className="text-sm text-muted-foreground">Nome</p>
                  <p className="font-medium">{selectedProduct.name}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">SKU</p>
                  <p className="font-mono">{selectedProduct.sku}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Código de Barras</p>
                  <p className="font-mono">{selectedProduct.barcode || '-'}</p>
                </div>
              </div>
              
              <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
                <div>
                  <p className="text-sm text-muted-foreground">Marca</p>
                  <p>{selectedProduct.brand || '-'}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Modelo</p>
                  <p>{selectedProduct.model || '-'}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Categoria</p>
                  <p data-testid="view-product-category-value">{getProductCategoryLabel(selectedProduct)}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Part Number</p>
                  <p className="font-mono text-sm">{selectedProduct.part_number || '-'}</p>
                </div>
              </div>

              {buildProductHighlightItems(selectedProduct).length > 0 && (
                <div className="rounded-lg border bg-slate-50 p-4" data-testid="view-product-main-attributes-section">
                  <h3 className="mb-3 font-medium text-slate-900">Atributos principais</h3>
                  <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
                    {buildProductHighlightItems(selectedProduct).map((item) => (
                      <div key={item.key} data-testid={`view-product-main-attribute-${item.key}`}>
                        <p className="text-sm text-muted-foreground">{item.label}</p>
                        <p className="font-medium">{item.value}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="grid gap-4 lg:grid-cols-2">
                {(() => {
                  const selectedProductSchema = getSchemaForCategory(getProductClassificationState(selectedProduct).categoryPrefix || selectedProduct.category);
                  return selectedProductSchema ? (
                    <div className="rounded-lg border bg-slate-50 p-4" data-testid="view-product-technical-specs-section">
                      <h3 className="mb-3 flex items-center gap-2 font-medium">
                        <Settings className="h-4 w-4 text-indigo-600" />
                        Especificações Técnicas - {selectedProductSchema.display_name}
                      </h3>
                      <DynamicSpecsForm
                        schema={selectedProductSchema}
                        specs={selectedProduct.technical_specs || selectedProduct.specs || {}}
                        onChange={() => {}}
                        readOnly
                      />
                    </div>
                  ) : null;
                })()}

                <ProductPosDescriptionConfigurator
                  category={getProductClassificationState(selectedProduct).categoryPrefix || selectedProduct.category}
                  value={selectedProduct}
                  brand={selectedProduct.brand}
                  model={selectedProduct.model}
                  specs={selectedProduct.technical_specs || selectedProduct.specs || {}}
                  onChange={() => {}}
                  readOnly
                  testIdPrefix="view-product"
                />
              </div>
              
              {/* Pricing */}
              <div className="grid gap-4 md:grid-cols-3">
                {canViewCost && (
                  <div>
                    <p className="text-sm text-muted-foreground">Preço de Custo</p>
                    <p className="font-medium">{formatCurrency(selectedProduct.cost_price)}</p>
                  </div>
                )}
                <div>
                  <p className="text-sm text-muted-foreground">Preço de Venda</p>
                  <p className="font-medium text-green-600">{formatCurrency(selectedProduct.unit_price)}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Stock Actual</p>
                  <p className="font-medium text-lg">{selectedProduct.current_stock}</p>
                </div>
              </div>
              
              {/* Stock by Location */}
              {Object.keys(getProductStockByLocationSnapshot(selectedProduct)).length > 0 && (
                <div className="border rounded-lg p-4 bg-slate-50">
                  <h3 className="font-medium mb-3 flex items-center gap-2">
                    <MapPin className="h-4 w-4 text-indigo-600" />
                    Stock por Localização
                  </h3>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                    {Object.entries(getProductStockByLocationSnapshot(selectedProduct)).map(([locName, data]) => {
                      const loc = locations.find(l => l.name === locName);
                      const IconComp = loc ? (LOCATION_TYPE_ICONS[loc.type] || MapPin) : MapPin;
                      return (
                        <div key={locName} className="bg-white rounded-lg p-3 border">
                          <div className="flex items-center gap-2 mb-1">
                            <IconComp className="h-4 w-4 text-slate-500" />
                            <span className="text-sm font-medium">{locName}</span>
                          </div>
                          <p className="text-2xl font-bold text-indigo-600">{data.count}</p>
                          <p className="text-xs text-muted-foreground">
                            {selectedProduct.is_trackable ? (data.count === 1 ? 'unidade' : 'unidades') : `${data.available ?? data.count} disponível`}
                          </p>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
              
              {/* Items list for trackable products */}
              {selectedProduct.is_trackable && (
                <div className="border rounded-lg p-4">
                  <div className="flex justify-between items-center mb-3">
                    <h3 className="font-medium">Items Registados ({productItems.length})</h3>
                    <div className="flex gap-2">
                      <Button 
                        size="sm" 
                        variant="outline"
                        onClick={() => setSerialNumbersDialogOpen(true)}
                        data-testid="manage-serial-numbers-btn"
                      >
                        <Hash className="h-3 w-3 mr-1" /> Gerir Nº Série
                      </Button>
                      <Button size="sm" onClick={() => openAddItemDialog(selectedProduct)}>
                        <Plus className="h-3 w-3 mr-1" /> Adicionar
                      </Button>
                    </div>
                  </div>
                  {productItems.length === 0 ? (
                    <p className="text-center text-muted-foreground py-4">Nenhum item registado</p>
                  ) : (
                    <div className="space-y-2 max-h-60 overflow-y-auto">
                      {productItems.map(item => (
                        <div 
                          key={item.id} 
                          className="flex justify-between items-center border rounded p-2 hover:bg-slate-50"
                        >
                          <div 
                            className="cursor-pointer flex-1"
                            onClick={() => navigate(`/items?search=${item.imei1 || item.serial_number}`)}
                          >
                            <p className="font-mono text-sm">{item.imei1 || item.serial_number}</p>
                            <p className="text-xs text-muted-foreground">
                              {item.condition}
                            </p>
                          </div>
                          <div className="flex items-center gap-2">
                            <Badge variant="outline" className="text-xs">
                              <MapPin className="h-3 w-3 mr-1" />
                              {item.location_name || item.location}
                            </Badge>
                            <Badge variant={item.state === 'in_stock' ? 'default' : 'secondary'}>
                              {item.state === 'in_stock' ? 'Em Stock' : item.state}
                            </Badge>
                            <LabelPrinter 
                              type="item" 
                              itemId={item.id}
                              variant="ghost"
                              size="sm"
                              showLabel={false}
                            />
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Serial Numbers Management Dialog */}
      <Dialog open={serialNumbersDialogOpen} onOpenChange={setSerialNumbersDialogOpen}>
        <DialogContent className="w-[min(92vw,1380px)] max-w-[1380px] max-h-[90vh] overflow-hidden" data-testid="serial-numbers-management-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Hash className="h-5 w-5 text-blue-600" />
              Gestão de Números de Série
            </DialogTitle>
          </DialogHeader>
          {selectedProduct && (
            <SerialNumbersPanel
              productId={selectedProduct.id}
              productName={selectedProduct.name}
              onClose={() => setSerialNumbersDialogOpen(false)}
            />
          )}
        </DialogContent>
      </Dialog>

      {/* Edit Product Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="w-[min(92vw,1480px)] max-w-[1480px] max-h-[90vh] overflow-y-auto p-4 sm:p-5 xl:p-6">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Edit className="h-5 w-5 text-amber-600" />
              Editar Produto - {selectedProduct?.name}
            </DialogTitle>
          </DialogHeader>
          
          {editFormData && (
            <div className="space-y-4">
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                <div className="flex items-start gap-2">
                  <AlertCircle className="h-5 w-5 text-amber-600 mt-0.5" />
                  <div>
                    <p className="font-medium text-amber-800">Edição Auditada</p>
                    <p className="text-sm text-amber-700">
                      Todas as alterações ficam registadas no histórico de auditoria.
                    </p>
                  </div>
                </div>
              </div>
              
              <div className="grid gap-4 md:grid-cols-2">
                <div className="space-y-2">
                  <Label>Nome</Label>
                  <Input
                    value={editFormData.name}
                    onChange={(e) => setEditFormData({...editFormData, name: e.target.value})}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Marca</Label>
                  <Input
                    value={editFormData.brand}
                    onChange={(e) => setEditFormData({...editFormData, brand: e.target.value})}
                  />
                </div>
              </div>
              
              <div className="grid gap-4 md:grid-cols-2">
                <div className="space-y-2">
                  <Label>Modelo</Label>
                  <Input
                    value={editFormData.model}
                    onChange={(e) => setEditFormData({...editFormData, model: e.target.value})}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Número de Peça</Label>
                  <Input
                    value={editFormData.part_number}
                    onChange={(e) => setEditFormData({...editFormData, part_number: e.target.value})}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Código de Barras</Label>
                  <Input
                    value={editFormData.barcode || ''}
                    onChange={(e) => setEditFormData({...editFormData, barcode: e.target.value.toUpperCase()})}
                    data-testid="edit-product-barcode-input"
                  />
                </div>
              </div>

              <div className="grid gap-4 md:grid-cols-2">
                <div className="space-y-2">
                  <Label>Categoria</Label>
                  <Select
                    value={editFormData.category || ''}
                    onValueChange={handleEditCategoryChange}
                  >
                    <SelectTrigger data-testid="edit-product-category-select">
                      <SelectValue placeholder="Selecionar categoria" />
                    </SelectTrigger>
                    <SelectContent>
                      {safeProductCategories.map((category) => {
                        const IconComp = CATEGORY_ICONS[category.base_prefix?.toUpperCase()] || Package;
                        return (
                          <SelectItem key={category.id} value={category.base_prefix}>
                            <div className="flex items-center gap-2">
                              <IconComp className="h-4 w-4" />
                              {category.name}
                            </div>
                          </SelectItem>
                        );
                      })}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>Subcategoria</Label>
                  <Select
                    value={editFormData.subcategory || ''}
                    onValueChange={(value) => setEditFormData({ ...editFormData, subcategory: value, invalidSubcategory: false })}
                    disabled={!editFormData.category || getSubcategoriesForCategory(editFormData.category).length === 0}
                  >
                    <SelectTrigger data-testid="edit-product-subcategory-select">
                      <SelectValue placeholder={editFormData.category ? 'Selecionar subcategoria' : 'Selecione categoria primeiro'} />
                    </SelectTrigger>
                    <SelectContent>
                      {getSubcategoriesForCategory(editFormData.category).map((subcategory) => (
                        <SelectItem key={subcategory.id} value={subcategory.id}>
                          {subcategory.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {editFormData.invalidSubcategory && (
                    <p className="text-xs text-amber-700" data-testid="edit-product-invalid-subcategory-warning">
                      A subcategoria guardada neste artigo já não coincide com a categoria actual. Seleccione uma nova subcategoria antes de guardar.
                    </p>
                  )}
                </div>
              </div>

              <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4 rounded-lg border bg-slate-50 p-4" data-testid="edit-product-main-attributes-section">
                {getPrimaryAttributeRules(editCurrentSchema).map((rule) => (
                  <div className="space-y-2" key={rule.key}>
                    <Label>{rule.label}{rule.required ? ' *' : ''}</Label>
                    <Input
                      value={editFormData[rule.key] || ''}
                      onChange={(e) => setEditFormData({ ...editFormData, [rule.key]: e.target.value })}
                      data-testid={`edit-product-${rule.key}-input`}
                    />
                    <div className="flex flex-wrap gap-2 text-xs text-slate-500">
                      {rule.highlight ? <Badge variant="outline">Destaque</Badge> : null}
                      {rule.variant_policy && rule.variant_policy !== 'attribute_only' ? (
                        <Badge variant="secondary">{VARIANT_POLICY_LABELS[rule.variant_policy] || rule.variant_policy}</Badge>
                      ) : null}
                    </div>
                  </div>
                ))}
              </div>

              {editCategoryGovernance && (
                <div className="rounded-lg border bg-slate-50 p-4" data-testid="edit-product-schema-governance-panel">
                  <div className="flex flex-wrap items-center gap-2">
                    <Badge variant="outline">Schema {editCurrentSchema?.display_name || editCategoryGovernance.name}</Badge>
                    <Badge variant="secondary">Versão {editCurrentSchema?.version || editCategoryGovernance.schema_version || 1}</Badge>
                    <Badge variant={editCategoryGovernance.specs_required ? 'default' : 'outline'}>
                      {editCategoryGovernance.specs_required ? 'Specs obrigatórias' : 'Specs opcionais'}
                    </Badge>
                  </div>
                  <p className="mt-3 text-sm text-slate-600" data-testid="edit-product-variant-policy-summary">
                    {getVariantPolicySummary(editCurrentSchema)}
                  </p>
                  <p className="mt-2 text-xs text-slate-500">
                    Se alterar um atributo com política “Variante obrigatória”, o sistema vai pedir criação de nova variante/SKU em vez de editar este artigo.
                  </p>
                </div>
              )}

              {editCurrentSchema && (
                <div className="rounded-lg border bg-slate-50 p-4" data-testid="edit-product-technical-specs-section">
                  <h3 className="mb-3 flex items-center gap-2 font-medium">
                    <Settings className="h-4 w-4 text-indigo-600" />
                    Especificações Técnicas - {editCurrentSchema.display_name}
                  </h3>
                  <DynamicSpecsForm
                    schema={editCurrentSchema}
                    specs={editFormData.specs || {}}
                    onChange={(newSpecs) => setEditFormData({ ...editFormData, specs: newSpecs })}
                    errors={specsErrors}
                  />
                </div>
              )}

              <ProductPosDescriptionConfigurator
                category={editFormData.category}
                catalogRule={editCatalogPosRule}
                value={editFormData}
                brand={editFormData.brand}
                model={editFormData.model}
                specs={editFormData.specs}
                onChange={(updates) => setEditFormData((prev) => ({ ...prev, ...updates }))}
                testIdPrefix="edit-product"
              />
              
              <div className="grid gap-4 md:grid-cols-3">
                <div className="space-y-2">
                  <Label>Preço de Venda (MT)</Label>
                  <Input
                    type="number"
                    value={editFormData.unit_price}
                    onChange={(e) => setEditFormData({...editFormData, unit_price: e.target.value})}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Preço de Custo (MT)</Label>
                  <Input
                    type="number"
                    value={editFormData.cost_price}
                    onChange={(e) => setEditFormData({...editFormData, cost_price: e.target.value})}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Nível de Reposição</Label>
                  <Input
                    type="number"
                    value={editFormData.reorder_level}
                    onChange={(e) => setEditFormData({...editFormData, reorder_level: e.target.value})}
                  />
                </div>
              </div>
              
              <div className="space-y-2">
                <Label>Descrição</Label>
                <Textarea
                  value={editFormData.description}
                  onChange={(e) => setEditFormData({...editFormData, description: e.target.value})}
                  rows={2}
                />
              </div>
              
              {/* Mandatory Justification */}
              <div className="space-y-2 border-t pt-4">
                <Label className="text-amber-700 font-medium">Justificação da Alteração *</Label>
                <Textarea
                  value={editJustification}
                  onChange={(e) => setEditJustification(e.target.value)}
                  placeholder="Descreva o motivo desta alteração (obrigatório)..."
                  rows={2}
                  className="border-amber-300 focus:border-amber-500"
                />
                <p className="text-xs text-muted-foreground">
                  Mínimo 5 caracteres. Esta justificação será registada no histórico.
                </p>
              </div>
              
              <div className="flex justify-end gap-2 pt-4 border-t">
                <Button variant="outline" onClick={() => setEditDialogOpen(false)}>
                  Cancelar
                </Button>
                <Button 
                  onClick={handleSaveEdit}
                  disabled={!editJustification || editJustification.trim().length < 5}
                  data-testid="edit-product-save-button"
                >
                  <Edit className="h-4 w-4 mr-2" />
                  Guardar Alterações
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Confirm Edit Dialog */}
      <AlertDialog open={confirmEditDialogOpen} onOpenChange={setConfirmEditDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertCircle className="h-5 w-5 text-amber-600" />
              Confirmar Alteração
            </AlertDialogTitle>
            <AlertDialogDescription className="space-y-2">
              <p>Esta alteração ficará registada no histórico de auditoria.</p>
              <p className="mt-2">
                <strong>Justificação:</strong> &ldquo;{editJustification}&rdquo;
              </p>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleConfirmEdit}>Confirmar</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Dialog open={bulkUpdateDialogOpen} onOpenChange={setBulkUpdateDialogOpen}>
        <DialogContent className="max-w-2xl" data-testid="inventory-bulk-update-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ArrowRightLeft className="h-5 w-5 text-sky-600" />
              Actualização em Massa via Excel
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            <div className="rounded-xl border bg-slate-50 p-3 text-sm text-slate-700" data-testid="inventory-bulk-update-scope-summary">
              <p className="font-medium">Âmbito actual</p>
              <p className="mt-1">{bulkUpdateScopeDescription}</p>
            </div>

            {selectedProductIds.length > 0 && (
              <div className="flex items-start gap-3 rounded-xl border border-slate-200 p-3" data-testid="inventory-bulk-update-selection-toggle-row">
                <Checkbox
                  checked={bulkUpdateUseSelection}
                  onCheckedChange={(checked) => setBulkUpdateUseSelection(Boolean(checked))}
                  data-testid="inventory-bulk-update-use-selection-checkbox"
                />
                <div>
                  <p className="text-sm font-medium text-slate-900">Usar apenas os produtos seleccionados</p>
                  <p className="text-xs text-slate-600">Desactive para exportar um template com todos os produtos do filtro actual.</p>
                </div>
              </div>
            )}

            <div className="grid gap-3 md:grid-cols-2">
              <Button
                variant="outline"
                onClick={handleDownloadBulkUpdateTemplate}
                disabled={bulkUpdateDownloading}
                data-testid="inventory-bulk-update-download-template-button"
              >
                <FileText className="mr-2 h-4 w-4" />
                {bulkUpdateDownloading ? 'A descarregar...' : 'Descarregar template update'}
              </Button>

              <div className="space-y-2">
                <Label htmlFor="inventory-bulk-update-file">Excel de actualização</Label>
                <Input
                  id="inventory-bulk-update-file"
                  type="file"
                  accept=".xlsx,.xls"
                  onChange={(event) => setBulkUpdateFile(event.target.files?.[0] || null)}
                  data-testid="inventory-bulk-update-file-input"
                />
              </div>
            </div>

            <Alert data-testid="inventory-bulk-update-supported-fields-alert">
              <AlertDescription>
                Campos suportados: preço, stock por local, nome, categoria, subcategoria, marca, modelo, part number, descrição, nível de reposição e local principal. Match principal por SKU + Local.
              </AlertDescription>
            </Alert>

            {bulkUpdateSummary && (
              <div className="rounded-xl border bg-white p-3" data-testid="inventory-bulk-update-summary-card">
                <div className="mb-3 flex items-center gap-2 text-xs">
                  <Badge variant={bulkUpdateSummary.preview_only ? 'secondary' : 'default'}>
                    {bulkUpdateSummary.preview_only ? 'Pré-validação visual' : 'Aplicação final'}
                  </Badge>
                  {bulkUpdateSummary.preview_only && <span className="text-slate-500">Nenhuma alteração foi gravada ainda.</span>}
                </div>
                <div className="flex flex-wrap items-center gap-2 text-xs">
                  <Badge variant="outline">Actualizados: {bulkUpdateSummary.updated || 0}</Badge>
                  <Badge variant="outline">Sem alterações: {bulkUpdateSummary.unchanged || 0}</Badge>
                  <Badge variant="outline">Erros: {bulkUpdateSummary.errors || 0}</Badge>
                  <Badge variant="outline">Linhas: {bulkUpdateSummary.rows_processed || 0}</Badge>
                </div>
                {(bulkUpdateSummary.results || []).some((item) => item.status === 'error') && (
                  <div className="mt-3 space-y-2" data-testid="inventory-bulk-update-error-list">
                    {(bulkUpdateSummary.results || []).filter((item) => item.status === 'error').slice(0, 6).map((item) => (
                      <div key={`${item.row_number}-${item.sku || 'sku'}`} className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">
                        Linha {item.row_number}{item.sku ? ` • ${item.sku}` : ''}: {item.message}
                      </div>
                    ))}
                  </div>
                )}
                {Array.isArray(bulkUpdateSummary.location_summary) && bulkUpdateSummary.location_summary.length > 0 && (
                  <div className="mt-3 space-y-2" data-testid="inventory-bulk-update-location-summary">
                    <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Resumo por local</p>
                    {bulkUpdateSummary.location_summary.map((entry) => (
                      <div key={`${entry.location_code}-${entry.location_id || 'sem-local'}`} className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-700">
                        <div className="flex flex-wrap items-center gap-2">
                          <span className="font-semibold">{entry.location_name}</span>
                          <Badge variant="outline">{entry.location_code}</Badge>
                          <Badge variant="outline">Linhas actualizadas: {entry.updated_rows || 0}</Badge>
                          <Badge variant="outline">Stocks alterados: {entry.stock_rows || 0}</Badge>
                          <Badge variant="outline">SKUs: {entry.affected_skus_count || 0}</Badge>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            <div className="flex justify-end gap-2 border-t pt-4">
              <Button variant="outline" onClick={() => setBulkUpdateDialogOpen(false)} data-testid="inventory-bulk-update-cancel-button">
                Fechar
              </Button>
              <Button variant="outline" onClick={handlePreviewBulkUpdate} disabled={!bulkUpdateFile || bulkUpdatePreviewing} data-testid="inventory-bulk-update-preview-button">
                {bulkUpdatePreviewing ? 'A pré-validar...' : 'Pré-validar Excel'}
              </Button>
              <Button onClick={handleExecuteBulkUpdate} disabled={!bulkUpdateFile || bulkUpdateUploading} data-testid="inventory-bulk-update-submit-button">
                {bulkUpdateUploading ? 'A actualizar...' : 'Aplicar actualização'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <AlertDialog open={bulkDeleteDialogOpen} onOpenChange={setBulkDeleteDialogOpen}>
        <AlertDialogContent data-testid="inventory-bulk-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <Trash2 className="h-5 w-5 text-red-600" />
              Apagar produtos em massa
            </AlertDialogTitle>
            <AlertDialogDescription className="space-y-4">
              <p>Vai processar <strong>{selectedProductIds.length}</strong> produto(s) seleccionados.</p>
              <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-800" data-testid="inventory-bulk-delete-warning-box">
                <p>Soft delete mantém histórico e permite restauro. Hard delete remove fisicamente o registo e só deve ser usado para libertar SKU após confirmação final.</p>
              </div>
              {isAdmin && (
                <div className="flex items-start gap-3 rounded-lg border border-slate-200 p-3" data-testid="inventory-bulk-delete-hard-delete-row">
                  <Checkbox
                    checked={bulkDeleteHardDelete}
                    onCheckedChange={(checked) => setBulkDeleteHardDelete(Boolean(checked))}
                    data-testid="inventory-bulk-delete-hard-delete-checkbox"
                  />
                  <div>
                    <p className="text-sm font-medium text-slate-900">Hard delete permanente</p>
                    <p className="text-xs text-slate-600">Disponível apenas para Admin. Produtos com stock ou items activos serão ignorados por segurança.</p>
                  </div>
                </div>
              )}
              <div className="space-y-2">
                <Label className="text-red-700 font-medium">Motivo da eliminação *</Label>
                <Textarea
                  value={bulkDeleteReason}
                  onChange={(event) => setBulkDeleteReason(event.target.value)}
                  placeholder="Descreva o motivo da operação em massa..."
                  rows={3}
                  className="border-red-300 focus:border-red-500"
                  data-testid="inventory-bulk-delete-reason-input"
                />
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="inventory-bulk-delete-cancel-button">Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmBulkDelete}
              className="bg-red-600 hover:bg-red-700"
              disabled={bulkDeleteReason.trim().length < 5 || bulkDeleteLoading}
              data-testid="inventory-bulk-delete-confirm-button"
            >
              {bulkDeleteLoading ? 'A processar...' : 'Confirmar eliminação'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete Product Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <Trash2 className="h-5 w-5 text-red-600" />
              Eliminar Produto
            </AlertDialogTitle>
            <AlertDialogDescription className="space-y-4">
              <p>
                Tem certeza que deseja eliminar o produto <strong>{selectedProduct?.name}</strong>?
              </p>
              <p className="text-sm">
                SKU: {selectedProduct?.sku} | Stock: {selectedProduct?.current_stock}
              </p>
              
              <div className="bg-red-50 border border-red-200 rounded-lg p-3 mt-3">
                <p className="text-sm text-red-800">
                  O produto será marcado como eliminado e poderá ser restaurado depois.
                </p>
                {isAdmin && (
                  <p className="mt-2 text-xs text-red-700" data-testid="inventory-delete-admin-stock-note">
                    Como Admin, pode fazer soft delete mesmo quando o artigo ainda tem stock. O hard delete continua reservado ao fluxo em massa e só quando não houver stock nem items activos.
                  </p>
                )}
              </div>
              
              <div className="space-y-2 mt-4">
                <Label className="text-red-700 font-medium">Motivo da Eliminação *</Label>
                <Textarea
                  value={deleteReason}
                  onChange={(e) => setDeleteReason(e.target.value)}
                  placeholder="Descreva o motivo da eliminação (obrigatório)..."
                  rows={2}
                  className="border-red-300 focus:border-red-500"
                />
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleConfirmDelete}
              className="bg-red-600 hover:bg-red-700"
              disabled={!deleteReason || deleteReason.trim().length < 5}
            >
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Audit Log Dialog */}
      <Dialog open={auditDialogOpen} onOpenChange={setAuditDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <History className="h-5 w-5 text-indigo-600" />
              Histórico de Alterações - {selectedProduct?.name}
            </DialogTitle>
          </DialogHeader>
          {auditLogs.length === 0 ? (
            <p className="text-center text-muted-foreground py-8">Nenhuma alteração registada</p>
          ) : (
            <div className="space-y-3">
              {auditLogs.map((log, index) => (
                <div key={log.id || `${log.product_id || 'product'}-${log.action || 'updated'}-${log.timestamp || 'sem-ts'}-${index}`} className="rounded-lg border bg-white p-3 shadow-sm" data-testid={`inventory-audit-log-row-${log.id || index}`}>
                  <div className="flex justify-between items-start mb-2">
                    <div>
                      <Badge variant={
                        log.action === 'created' ? 'default' :
                        log.action === 'edited' ? 'secondary' :
                        log.action === 'updated' ? 'secondary' :
                        log.action === 'stock_adjusted' ? 'secondary' :
                        log.action === 'deleted' ? 'destructive' :
                        log.action === 'restored' ? 'outline' : 'secondary'
                      }>
                        {log.action === 'created' && 'Criado'}
                        {log.action === 'edited' && 'Editado'}
                        {log.action === 'updated' && 'Actualizado'}
                        {log.action === 'stock_adjusted' && 'Stock Ajustado'}
                        {log.action === 'deleted' && 'Eliminado'}
                        {log.action === 'restored' && 'Restaurado'}
                      </Badge>
                    </div>
                    <span className="text-xs text-muted-foreground">
                      {formatDateTime(log.timestamp)}
                    </span>
                  </div>
                  <div className="flex items-center gap-2 text-sm">
                    <User className="h-4 w-4 text-slate-400" />
                    <span>{log.user_name}</span>
                    <Badge variant="outline" className="text-xs">{log.user_role}</Badge>
                  </div>
                  {log.justification && (
                    <p className="text-sm mt-2 text-slate-600 italic">
                      &ldquo;{log.justification}&rdquo;
                    </p>
                  )}
                  {log.changes && log.changes.length > 0 && (
                    <div className="mt-2 text-xs space-y-1">
                      {log.changes.map((change) => (
                        <div key={`${change.field || change.field_label}-${String(change.old_value)}-${String(change.new_value)}`} className="flex items-center gap-2">
                          <span className="text-muted-foreground">{change.field_label || change.field}:</span>
                          <span className="line-through text-red-600">{formatAuditValue(change.old_value ?? change.from)}</span>
                          <span>→</span>
                          <span className="text-green-600">{formatAuditValue(change.new_value ?? change.to)}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
